/**
 * standOverviewService.js — "Proje Stand Listesi" Servis Dosyası
 *
 * Route: /Management/Projects/Stands (StandListPage.jsx)
 * Base URL (future): https://gw.sakagrup.com:8443/api/v1/stands/overview
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 *
 * KURAL: Bu liste, bir fuardaki TÜM standları döner — bazılarının henüz bir
 * inşaat projesi (Projects modülü) yoktur, bu durumda firma/dekoratör/proje
 * alanları null gelir ve UI'da "Proje kaydı yok" olarak gösterilir.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
// Stand adının tek tarifi — hol adı + "." + kod. Yazma uçları 2026-09-03'ten beri
// `standCode` alıyor ve adı sunucu kuruyor (bkz. standNaming.js).
import { standCodeFromName, composeStandName } from './standNaming';
import i18n from "../i18n";
import { planYazimlari } from '../Components/Projects/kusbakisiYardimci';

const BASE = `${JAVA_BACKEND_API_URL}/stands/overview`;

// ─── LİSTE PİLLERİ (bkz. docs/guides/missing-pills-guide.md) ──────────────────
// 2026-08-29: GET /standTalep/list artık pil alanlarının tamamını taşıyor
// (stand_type ağacının 4 seviyesi, standCode/firmaShortName/firmaNation/tabelaAdi,
// standArea/projectHeightNumber/kw, constructionPermit/commitments/workPermit).
// Eklemeler TAMAMEN additive — DTO'nun eski 20 alanı aynı isim/tip/null davranışında.

// İnşa İzni durumları (stand_status). "Gerek yok" ile "Verilmedi" ikisi de
// granted:false ama zıt anlamlı — bu yüzden izin VAR sayılan id'ler ayrı tutulur.
// 1 İnşa İznine Gerek yok · 4 İzin Verildi · 7 Nehir İSGM Tarafından Uygun Görüldü
export const CONSTRUCTION_PERMIT_GRANTED_STATUS_IDS = [1, 4, 7];

// Taahhütname üretim zinciri. Bugün CHOSEN'da duruyor: PDF üretimi henüz yok, bu
// yüzden GENERATED/SIGNATURE_REQUIRED ulaşılamaz; SIGNED sadece elle imzalı dosya
// taşıyan eski projelerde görülür.
export const COMMITMENT_STATE = {
  NO_SOURCE:          { get label() { return i18n.t("services:standOverviewService.kaynakBelgeYok"); },   color: 'secondary' },
  SOURCE_UPLOADED:    { get label() { return i18n.t("services:standOverviewService.kaynakYuklendi"); },    color: 'warning' },
  CHOSEN:             { get label() { return i18n.t("services:standOverviewService.kaynakSecildi"); },     color: 'info' },
  // #217 / #216: the stand's decorator approved the taahhutname generated from the chosen source - counts for the Insa Izni.
  COMPANY_APPROVED:   { get label() { return i18n.t("services:standOverviewService.firmaOnayladi"); },       color: 'success' },
  GENERATED:          { get label() { return i18n.t("services:standOverviewService.taahhutnameUretildi"); }, color: 'info' },
  SIGNATURE_REQUIRED: { get label() { return i18n.t("services:standOverviewService.imzaBekliyor"); },      color: 'warning' },
  SIGNED:             { get label() { return i18n.t("services:standOverviewService.imzali"); },             color: 'success' },
};

// Stand tipi pilleri hem MOCK kodlarıyla ('paket'/'moduler'/'maxima') hem de CANLI
// adlarıyla ('Paket Stand'/'Modüler'/'Maxima Kare Profil') gelebilir. Canlı taraf
// kapalı bir küme DEĞİL (TRUSS, Örümcek Stand, iki ayrı Sunta girdisi; 'agac' hiç
// yok) ve adlar staff tarafından düzenlenebilir — bu yüzden bilinen bir kod ise
// yerelleştirilmiş etiketi, değilse gelen adı OLDUĞU GİBİ basıyoruz.
export function resolvePillLabel(value, codeLabels) {
  if (value == null || value === '') return null;
  return codeLabels[value] ?? String(value);
}

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

// Mock kayıtlardaki fuarId alanı sadece bilgi amaçlıdır, filtrelemede KULLANILMAZ
// (bkz. getStandOverviewList/getDecoratorFirmaOptions). Önceki sürüm bu değeri
// modül yüklenirken localStorage'dan bir kereliğine okuyup donduruyordu ("taklit"
// etmeye çalışıyordu); ama gerçek selectedFuarId hem girişten sonra aktif-fuar
// API'sinden (MainContext.fetchActiveFairId) hem de Header'daki fuar değiştirme
// modalından (handleActivateFair) modül yüklendikten SONRA güncellenebiliyor — bu
// da dondurulmuş değerle gerçek seçili fuarı neredeyse hep uyumsuz bırakıp listeyi
// sessizce (hatasız) boşaltıyordu. Mock kayıtlar tek bir demo fuarını temsil ettiği
// için artık hangi gerçek fuar seçili olursa olsun gösteriliyor.
const _mockFuarId = Number(
  (typeof window !== 'undefined' && window.localStorage.getItem('activeFairId')) || 3
) || 3;

const _mockStandsRaw = [
  // ─── Aşağıdaki 3 kayıt "Test Decorator" (firmaProfileService.js'teki
  // MOCK_DECORATOR_FIRMA_ID = 500) firma-ana-sayfa dashboard'ının İnşa İzin
  // Durumu / Proje Evrakları Durumu panelleri için eklendi — bu dekoratörün
  // müşteri stand roster'ı. standNo/standCode, referans görseldeki örnek
  // kodlarla eşleşecek şekilde seçildi (_standListDesignOverrides altında).
  // Listenin BAŞINDA duruyorlar (standId sırasına göre değil) ki projectService.js'e
  // standId köprüsüyle bağlı — bkz. getProjectByStandId — tek 3 kayıt sayfa 2'ye
  // gitmeden, Stand Listesi'nin varsayılan görünümünde (sayfa 1) hemen görünsün.
  {
    id: '665f1a2b3c4d5e6f7a8b9c24',
    standId: 323,
    standName: 'A-201',
    salonName: 'Salon 1',
    firmaId: 73,
    firmaName: 'ÇARŞIBAŞI KOZMETİK TEKSTİL LTD. ŞTİ.',
    dekoratorFirmaId: 500,
    dekoratorFirmaName: 'Test Decorator',
    standTypeId: 5,
    standHeightId: 2,
    projectHeightNumber: 3.00,
    standArea: 22,
    floorInfo: 'Tek Katlı',
    statusId: 4,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-07-08T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-07-08T11:00:00.000+00:00' },
      { standFileId: 3, status: 1, approvedBy: 7, updatedAt: '2026-07-08T12:00:00.000+00:00' },
    ],
    requiredCount: 3,
    approvedCount: 3,
    // Teknik Talep Planı (standFileId 3) — standTalepService.js'teki bu standın
    // Elektrik/Su/Hava/İnternet kayıtları zaten hasRequest:true (bkz. _mockTalepler),
    // bu da belgenin daha önce gönderilmiş olması gerektiği anlamına gelir (bkz.
    // standTalepService.js'teki 2026-07-23 notu: belge yüklenmeden talep
    // tamamlanamaz) — bu kayıt olmadan TeknikTalepModal belgeyi yanlışlıkla "eksik"
    // gösterip zaten talep edilmiş bir kategori için gereksiz yeniden yükleme isteyebilirdi.
    uploadedFiles: [
      {
        id: '665f1a2b3c4d5e6f7a8b9c31',
        standFileId: 3,
        subStandFileId: null,
        storedFileName: '1720425000000_teknik_talep_plani.pdf',
        originalFileName: 'teknik_talep_plani.pdf',
        approvalStatus: 1,
        choosenToGenerate: false,
        uploadedBy: 73,
        uploadedAt: '2026-07-08T09:30:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
    ],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    // Stant Firmaları (FirmaStandDetailPage.jsx "Stant Firmaları" sekmesi) — admin
    // tarafındaki standService.js/StandDetailPage.jsx'teki aynı isimli tablonun
    // salt-okunur firma karşılığı, standId köprüsü yok, doğrudan bu kayda ait.
    firmalar: [
      { firmaId: 500, firmaName: 'Test Decorator', firmaTypeName: 'Dekoratör', eslesmeSeviyesi: 1 },
      { firmaId: 73, firmaName: 'ÇARŞIBAŞI KOZMETİK TEKSTİL LTD. ŞTİ.', firmaTypeName: 'Katılımcı', eslesmeSeviyesi: 1 },
    ],
    // Stand Durumu — admin'in StandDetailPage.jsx'teki aynı 8 alanının salt-okunur
    // firma karşılığı (bkz. FirmaStandDetailPage.jsx "Stand Durumu" bölümü).
    standDurumu: {
      problemliStand: 'hayir', icTesisat: 'var', yasakliStand: 'hayir', islakImza: 'var',
      alanBorcu: 'yok', projeBorcu: 'yok', internetTalebi: 'var', camKullanimi: 'yok',
    },
    // Kategori bazlı denetim geçmişi (standDurumuService.js'teki DENETIM_KATEGORILERI:
    // İSG/Proje/BetterStands) — her kategori kendi `kayitlar` (ziyaret geçmişi)
    // dizisini taşır, Stand Durumu sekmesindeki accordion bunları kategori başına
    // gösterir (bkz. DenetimDurumuAccordion, FirmaStandOverviewPage.jsx). İSG'de
    // eski bir uygunsuzluğun daha sonra uygun bir ziyaretle kapandığı örnek —
    // denetimDurumuOk sadece SON kaydı dikkate alır, bu yüzden bu stand yine geçti
    // sayılır. Bu standı (A-201) Denetim Durumu için "tamam" örneği yapan üç kategori
    // de en son ziyarette 'uygun'.
    denetimler: [
      { kategori: 'ISG', kayitlar: [
        { id: 1, sonuc: 'uygunsuzluk', tarih: '2026-06-20T10:00:00.000+00:00', denetci: 'Zeynep Demir' },
        { id: 2, sonuc: 'uygun', tarih: '2026-07-09T10:00:00.000+00:00', denetci: 'Ahmet Yılmaz' },
      ] },
      { kategori: 'Proje', kayitlar: [
        { id: 3, sonuc: 'uygun', tarih: '2026-07-10T11:00:00.000+00:00', denetci: 'Zeynep Demir' },
      ] },
      { kategori: 'BetterStands', kayitlar: [
        { id: 4, sonuc: 'uygun', tarih: '2026-07-11T09:30:00.000+00:00', denetci: 'Ahmet Yılmaz' },
      ] },
    ],
    // Kategorilerden bağımsız, stand başına tek bayrak (bkz. standDurumuService.js
    // dosya başı yorumu) — Denetim Durumu'nun geçme şartlarından biri.
    kontrolFormu: { gecti: true, tarih: '2026-07-11T09:00:00.000+00:00', denetci: 'Ahmet Yılmaz' },
    // Fotoğraflar — "Stant Bilgileri" sekmesindeki salt-okunur galeri (bkz.
    // FirmaStandOverviewPage.jsx'teki FotograflarSection). Gerçek görsel dosyası/URL'i
    // yok, sadece başlık + tarih — bu sayfada bir yükleme akışı olmadığı için.
    photos: [
      { id: 'ph-323-1', caption: 'Genel Görünüm', uploadedAt: '2026-07-15T10:00:00.000+00:00' },
      { id: 'ph-323-2', caption: 'İç Mekan', uploadedAt: '2026-07-16T11:30:00.000+00:00' },
      { id: 'ph-323-3', caption: 'Cephe', uploadedAt: '2026-07-17T09:15:00.000+00:00' },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-08T09:00:00.000+00:00',
    updatedAt: '2026-07-08T12:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c25',
    standId: 324,
    standName: 'B-11',
    salonName: 'Salon 2',
    firmaId: 74,
    firmaName: 'ABC TEKSTİL LTD.',
    dekoratorFirmaId: 500,
    dekoratorFirmaName: 'Test Decorator',
    standTypeId: 6,
    standHeightId: 2,
    projectHeightNumber: 3.20,
    standArea: 26,
    floorInfo: 'Tek Katlı',
    statusId: 2,
    dealStatus: 1,
    financeApproval: 0,
    financeText: null,
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-07-09T10:00:00.000+00:00' },
      { standFileId: 2, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 3,
    approvedCount: 1,
    // Teknik Talep Planı (standFileId 3) — bu standın Elektrik/İnternet kayıtları
    // zaten hasRequest:true (bkz. standTalepService.js'teki _mockTalepler), demek ki
    // belge daha önce gönderilmiş (henüz onaylanmamış, bkz. yukarıdaki fileApprovals
    // status:0) — aynı gerekçe A-201'deki (id c24) not ile aynı.
    uploadedFiles: [
      {
        id: '665f1a2b3c4d5e6f7a8b9c32',
        standFileId: 3,
        subStandFileId: null,
        storedFileName: '1720512000000_teknik_talep_plani.pdf',
        originalFileName: 'teknik_talep_plani.pdf',
        approvalStatus: 0,
        choosenToGenerate: false,
        uploadedBy: 74,
        uploadedAt: '2026-07-09T09:30:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
    ],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    firmalar: [
      { firmaId: 500, firmaName: 'Test Decorator', firmaTypeName: 'Dekoratör', eslesmeSeviyesi: 1 },
      { firmaId: 74, firmaName: 'ABC TEKSTİL LTD.', firmaTypeName: 'Katılımcı', eslesmeSeviyesi: 1 },
    ],
    // problemliStand: 'evet' — bu stand (B-11), Stand Durumu sekmesinin "sorunlu"
    // örneği olarak seçildi (bkz. /revise-page ile eklenen standDurumuService.js):
    // proje/borç/talep durumlarının hepsi zaten ayrı ayrı sorunlu, admin bayrağı da
    // aynı yönde işaretlendi.
    standDurumu: {
      problemliStand: 'evet', icTesisat: 'yok', yasakliStand: 'hayir', islakImza: 'yok',
      alanBorcu: 'var', projeBorcu: 'var', internetTalebi: 'yok', camKullanimi: 'yok',
    },
    // Bu stand (B-11) Denetim Durumu için "sorunlu" örneği — İSG'nin tek ziyareti
    // uygunsuzluk, Proje/BetterStands'te henüz hiç ziyaret yok, Kontrol Formu da
    // uygulanmadı (bkz. altındaki kontrolFormu: null).
    denetimler: [
      { kategori: 'ISG', kayitlar: [
        { id: 1, sonuc: 'uygunsuzluk', tarih: '2026-07-09T13:00:00.000+00:00', denetci: 'Ahmet Yılmaz' },
      ] },
      { kategori: 'Proje', kayitlar: [] },
      { kategori: 'BetterStands', kayitlar: [] },
    ],
    kontrolFormu: null,
    // Fotoğraflar — bkz. A-201 (id c24) kaydındaki aynı isimli yorum.
    photos: [
      { id: 'ph-324-1', caption: 'Genel Görünüm', uploadedAt: '2026-07-10T08:45:00.000+00:00' },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-09T09:00:00.000+00:00',
    updatedAt: '2026-07-09T10:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c26',
    standId: 325,
    standName: 'C-19',
    salonName: 'Salon 3',
    firmaId: 75,
    firmaName: 'DEF MOBİLYA A.Ş.',
    dekoratorFirmaId: 500,
    dekoratorFirmaName: 'Test Decorator',
    standTypeId: 5,
    standHeightId: 1,
    projectHeightNumber: 2.50,
    standArea: 18,
    floorInfo: 'Tek Katlı',
    statusId: 2,
    dealStatus: 1,
    financeApproval: 0,
    financeText: null,
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [],
    requiredCount: 3,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    firmalar: [
      { firmaId: 500, firmaName: 'Test Decorator', firmaTypeName: 'Dekoratör', eslesmeSeviyesi: 1 },
      { firmaId: 75, firmaName: 'DEF MOBİLYA A.Ş.', firmaTypeName: 'Katılımcı', eslesmeSeviyesi: 1 },
    ],
    standDurumu: {
      problemliStand: 'evet', icTesisat: 'yok', yasakliStand: 'hayir', islakImza: 'yok',
      alanBorcu: 'var', projeBorcu: 'var', internetTalebi: 'yok', camKullanimi: 'yok',
    },
    denetimler: [],
    kontrolFormu: null,
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-10T09:00:00.000+00:00',
    updatedAt: '2026-07-10T09:00:00.000+00:00',
  },
  // "Test Decorator" roster'ına, henüz hiç proje bilgisi girilmemiş bir stand —
  // FirmaProjelerListPage.jsx'teki "Proje Yükle" akışının demo örneği (bkz.
  // projectDeclared: false + createStandProject). Katılımcı firma (firmaId) ve
  // dekoratör ataması zaten yapılmış, sadece dekoratörün gireceği proje bilgileri
  // (hMax/en/boy/kw, bkz. dosya sonu createStandProject) eksik — bu yüzden
  // standCategory/projectType/standArea/projectHeightNumber hâlâ null.
  {
    id: '665f1a2b3c4d5e6f7a8b9c27',
    standId: 326,
    standName: 'D-05',
    salonName: 'Salon 4',
    firmaId: 76,
    firmaName: 'YEŞİL BAHÇE PEYZAJ ÜRÜNLERİ LTD. ŞTİ.',
    dekoratorFirmaId: 500,
    dekoratorFirmaName: 'Test Decorator',
    standTypeId: null,
    standHeightId: null,
    projectHeightNumber: null,
    standArea: null,
    floorInfo: null,
    statusId: null,
    dealStatus: null,
    financeApproval: null,
    financeText: null,
    serinProje: false,
    requiredFileIds: [],
    fileApprovals: [],
    requiredCount: 0,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [],
    firmalar: [
      { firmaId: 500, firmaName: 'Test Decorator', firmaTypeName: 'Dekoratör', eslesmeSeviyesi: 1 },
      { firmaId: 76, firmaName: 'YEŞİL BAHÇE PEYZAJ ÜRÜNLERİ LTD. ŞTİ.', firmaTypeName: 'Katılımcı', eslesmeSeviyesi: 1 },
    ],
    standDurumu: {
      problemliStand: 'hayir', icTesisat: 'yok', yasakliStand: 'hayir', islakImza: 'yok',
      alanBorcu: 'yok', projeBorcu: 'yok', internetTalebi: 'yok', camKullanimi: 'yok',
    },
    denetimler: [],
    kontrolFormu: null,
    revisions: [],
    sustainability: null,
    projectDeclared: false,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-15T09:00:00.000+00:00',
    updatedAt: '2026-07-15T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c0d',
    standId: 300,
    standName: 'A-101',
    salonName: 'Salon 1',
    firmaId: 55,
    firmaName: 'ACME Mobilya A.Ş.',
    dekoratorFirmaId: 66,
    dekoratorFirmaName: 'Yıldız Dekorasyon Ltd.',
    standTypeId: 5,
    standHeightId: 2,
    projectHeightNumber: 3.50,
    // standArea/floorInfo: henüz backend'de yok, ileride eklenecek — şimdilik mock.
    standArea: 24,
    floorInfo: 'Tek Katlı',
    statusId: 2,
    dealStatus: 2,
    financeApproval: 0,
    financeText: null,
    serinProje: false,
    requiredFileIds: [1, 2, 3, 5],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-07-05T14:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-07-04T09:00:00.000+00:00' },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 5, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 4,
    approvedCount: 2,
    uploadedFiles: [
      {
        id: '665f1a2b3c4d5e6f7a8b9c10',
        standFileId: 1,
        subStandFileId: null,
        storedFileName: '1720079000000_9f8e7d6c.pdf',
        originalFileName: 'malzeme_listesi.pdf',
        approvalStatus: 1,
        choosenToGenerate: true,
        uploadedBy: 55,
        uploadedAt: '2026-07-03T10:00:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
      // Statik Hesap — tek dosya, onaylı.
      {
        id: '665f1a2b3c4d5e6f7a8b9c11',
        standFileId: 2,
        subStandFileId: null,
        storedFileName: '1720079050000_b2c3d4.pdf',
        originalFileName: 'statik_hesap_raporu.pdf',
        approvalStatus: 1,
        choosenToGenerate: true,
        uploadedBy: 55,
        uploadedAt: '2026-07-04T09:00:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
      // Teknik Talep Planı (Elektrik Planı) — 2 dosya, ikisi de beklemede.
      {
        id: '665f1a2b3c4d5e6f7a8b9c15',
        standFileId: 3,
        subStandFileId: null,
        storedFileName: '1720079060000_c3d4e5.jpg',
        originalFileName: 'elektrik_plani_gorseli.jpg',
        approvalStatus: 0,
        choosenToGenerate: false,
        uploadedBy: 55,
        uploadedAt: '2026-07-04T09:30:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
      {
        id: '665f1a2b3c4d5e6f7a8b9c16',
        standFileId: 3,
        subStandFileId: null,
        storedFileName: '1720079061000_d4e5f6.pdf',
        originalFileName: 'elektrik_plani.pdf',
        approvalStatus: 0,
        choosenToGenerate: true,
        uploadedBy: 55,
        uploadedAt: '2026-07-04T09:31:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
      // Proje Görselleri — 4 açıdan zorunlu kroki fotoğrafı (subStandFileId: 1=sol üst, 2=sağ üst, 3=sol alt, 4=sağ alt)
      {
        id: '665f1a2b3c4d5e6f7a8b9c12',
        standFileId: 5,
        subStandFileId: 1,
        storedFileName: '1720079100000_a1b2c3.jpg',
        originalFileName: 'proje_gorselleri_top_left.jpg',
        approvalStatus: 1,
        choosenToGenerate: true,
        uploadedBy: 55,
        uploadedAt: '2026-07-06T08:00:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
      {
        id: '665f1a2b3c4d5e6f7a8b9c13',
        standFileId: 5,
        subStandFileId: 2,
        storedFileName: '1720079101000_d4e5f6.jpg',
        originalFileName: 'proje_gorselleri_top_right.jpg',
        approvalStatus: 2,
        choosenToGenerate: false,
        uploadedBy: 55,
        uploadedAt: '2026-07-06T08:00:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
      {
        id: '665f1a2b3c4d5e6f7a8b9c14',
        standFileId: 5,
        subStandFileId: 4,
        storedFileName: '1720079102000_g7h8i9.jpg',
        originalFileName: 'proje_gorselleri_bottom_right.jpg',
        approvalStatus: 2,
        choosenToGenerate: false,
        uploadedBy: 55,
        uploadedAt: '2026-07-06T08:00:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
      // subStandFileId: 3 (sol alt) kasıtlı olarak eksik — "yüklenmedi" durumunu göstermek için.
      {
        id: '665f1a2b3c4d5e6f7a8b9c30',
        standFileId: 5,
        subStandFileId: 5,
        storedFileName: '1720079103000_j1k2l3.jpg',
        originalFileName: 'proje_gorselleri_kusbakisi.jpg',
        approvalStatus: 0,
        choosenToGenerate: false,
        uploadedBy: 55,
        uploadedAt: '2026-07-06T08:05:00.000+00:00',
        deleted: false,
        deletedBy: null,
        deletedAt: null,
      },
    ],
    textContents: [
      { standFileId: 1, title: 'Malzeme', text: 'Ahşap ve cam.', updatedAt: '2026-07-03T10:05:00.000+00:00' },
    ],
    // Proje Taahütnameleri — henüz backend'de yok, ileride eklenecek — şimdilik mock.
    // fileId: inceleyen kişinin Proje Evrakları sekmesinden bu taahhütname için "ana
    // belge" seçtiği uploadedFiles.id — demo amaçlı statik için önceden seçilmiş.
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0, fileId: null },
      { key: 'statik', label: 'Statik Taahütname', status: 0, fileId: '665f1a2b3c4d5e6f7a8b9c11' },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0, fileId: null },
    ],
    revisions: [
      { id: '665f1a2b3c4d5e6f7a8b9c20', text: 'Kroki güncellendi.', userId: 7, createdAt: '2026-07-05T15:00:00.000+00:00' },
    ],
    sustainability: {
      definitionId: '665f0000000000000000ab01',
      definitionVersion: 2,
      answers: [
        { questionKey: 'pct_wood', number: 60.0, bool: null, choice: null, choices: null, text: null },
        { questionKey: 'reusable', number: null, bool: true, choice: null, choices: null, text: null },
      ],
      updatedAt: '2026-07-06T09:00:00.000+00:00',
      updatedBy: 55,
    },
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-01T09:00:00.000+00:00',
    updatedAt: '2026-07-06T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c0e',
    standId: 301,
    standName: 'A-102',
    salonName: 'Salon 1',
    firmaId: 56,
    firmaName: 'TechShow Ltd.',
    dekoratorFirmaId: 89,
    dekoratorFirmaName: 'ModernStand İnş.',
    standTypeId: 6,
    standHeightId: 3,
    projectHeightNumber: 5.00,
    standArea: 36,
    floorInfo: 'Çift Katlı',
    statusId: 4,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: true,
    requiredFileIds: [1, 2, 3, 4],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-20T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-06-21T10:00:00.000+00:00' },
      { standFileId: 3, status: 1, approvedBy: 7, updatedAt: '2026-06-22T10:00:00.000+00:00' },
      { standFileId: 4, status: 1, approvedBy: 7, updatedAt: '2026-06-23T10:00:00.000+00:00' },
    ],
    requiredCount: 4,
    approvedCount: 4,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    revisions: [
      { id: '665f1a2b3c4d5e6f7a8b9c21', text: 'Tüm belgeler onaylandı, üretime hazır.', userId: 7, createdAt: '2026-06-23T11:00:00.000+00:00' },
    ],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-15T09:00:00.000+00:00',
    updatedAt: '2026-06-23T10:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c0f',
    standId: 302,
    standName: 'B-05',
    salonName: 'Salon 2',
    firmaId: 57,
    firmaName: 'Global Expo GmbH',
    dekoratorFirmaId: null,
    dekoratorFirmaName: null,
    standTypeId: 5,
    standHeightId: 1,
    projectHeightNumber: 2.50,
    standArea: 18,
    floorInfo: 'Tek Katlı',
    statusId: 3,
    dealStatus: 3,
    financeApproval: 2,
    financeText: 'Katılmıyor.',
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [],
    requiredCount: 3,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 2 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-18T08:00:00.000+00:00',
    updatedAt: '2026-06-18T08:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c10',
    standId: 303,
    standName: 'B-06',
    salonName: 'Salon 2',
    firmaId: null,
    firmaName: null,
    dekoratorFirmaId: null,
    dekoratorFirmaName: null,
    standTypeId: null,
    standHeightId: null,
    projectHeightNumber: null,
    standArea: null,
    floorInfo: null,
    statusId: null,
    dealStatus: null,
    financeApproval: null,
    financeText: null,
    serinProje: false,
    requiredFileIds: [],
    fileApprovals: [],
    requiredCount: 0,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-01T09:00:00.000+00:00',
    updatedAt: '2026-06-01T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c11',
    standId: 304,
    standName: 'C-12',
    salonName: 'Salon 3',
    firmaId: null,
    firmaName: null,
    dekoratorFirmaId: null,
    dekoratorFirmaName: null,
    standTypeId: null,
    standHeightId: null,
    projectHeightNumber: null,
    standArea: null,
    floorInfo: null,
    statusId: null,
    dealStatus: null,
    financeApproval: null,
    financeText: null,
    serinProje: false,
    requiredFileIds: [],
    fileApprovals: [],
    requiredCount: 0,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-02T09:00:00.000+00:00',
    updatedAt: '2026-06-02T09:00:00.000+00:00',
  },
  // ─── Aşağıdaki kayıtlar dekoratör bazlı listeleme (eski DecoratorStandListPage, 2026-09-15'te kaldırıldı)
  // senaryosunu zenginleştirmek için eklendi: aynı dekoratör, farklı katılımcı
  // firmalar için birden çok stand işletebiliyor.
  {
    id: '665f1a2b3c4d5e6f7a8b9c12',
    standId: 305,
    standName: 'A-103',
    salonName: 'Salon 1',
    firmaId: 58,
    firmaName: 'Nordic Style Dizayn Ltd.',
    dekoratorFirmaId: 66,
    dekoratorFirmaName: 'Yıldız Dekorasyon Ltd.',
    standTypeId: 5,
    standHeightId: 2,
    projectHeightNumber: 3.00,
    standArea: 20,
    floorInfo: 'Tek Katlı',
    statusId: 1,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-25T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-06-25T11:00:00.000+00:00' },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 3,
    approvedCount: 2,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-25T09:00:00.000+00:00',
    updatedAt: '2026-06-25T11:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c13',
    standId: 306,
    standName: 'B-07',
    salonName: 'Salon 2',
    firmaId: 59,
    firmaName: 'Anka Havacılık San. Tic. A.Ş.',
    dekoratorFirmaId: 89,
    dekoratorFirmaName: 'ModernStand İnş.',
    standTypeId: 6,
    standHeightId: 3,
    projectHeightNumber: 4.50,
    standArea: 32,
    floorInfo: 'Çift Katlı',
    statusId: 2,
    dealStatus: 1,
    financeApproval: 0,
    financeText: null,
    serinProje: true,
    requiredFileIds: [1, 2, 3, 4],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-27T10:00:00.000+00:00' },
      { standFileId: 2, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 4, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 4,
    approvedCount: 1,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-27T09:00:00.000+00:00',
    updatedAt: '2026-06-27T10:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c14',
    standId: 307,
    standName: 'C-15',
    salonName: 'Salon 3',
    firmaId: 60,
    firmaName: 'Prestij Gıda Ürünleri Ltd.',
    dekoratorFirmaId: 130,
    dekoratorFirmaName: 'Prestij Fuarcılık Dekorasyon',
    standTypeId: 5,
    standHeightId: 1,
    projectHeightNumber: 2.50,
    standArea: 16,
    floorInfo: 'Tek Katlı',
    statusId: 4,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: false,
    requiredFileIds: [1, 2],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-10T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-06-10T11:00:00.000+00:00' },
    ],
    requiredCount: 2,
    approvedCount: 2,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-10T09:00:00.000+00:00',
    updatedAt: '2026-06-10T11:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c15',
    standId: 308,
    standName: 'C-16',
    salonName: 'Salon 3',
    firmaId: 61,
    firmaName: 'Vega Enerji Sistemleri A.Ş.',
    dekoratorFirmaId: 130,
    dekoratorFirmaName: 'Prestij Fuarcılık Dekorasyon',
    standTypeId: 6,
    standHeightId: 2,
    projectHeightNumber: 3.50,
    standArea: 28,
    floorInfo: 'Tek Katlı',
    statusId: 2,
    dealStatus: 2,
    financeApproval: 0,
    financeText: null,
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [],
    requiredCount: 3,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-12T09:00:00.000+00:00',
    updatedAt: '2026-06-12T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c16',
    standId: 309,
    standName: 'D-02',
    salonName: 'Salon 4',
    firmaId: 62,
    firmaName: 'Lotus Tekstil Konfeksiyon',
    dekoratorFirmaId: 141,
    dekoratorFirmaName: 'MaxExpo Yapı Dekorasyon',
    standTypeId: 5,
    standHeightId: 2,
    projectHeightNumber: 3.00,
    standArea: 22,
    floorInfo: 'Tek Katlı',
    statusId: 1,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: true,
    requiredFileIds: [1, 2, 3, 5],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-07-01T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-07-01T11:00:00.000+00:00' },
      { standFileId: 3, status: 1, approvedBy: 7, updatedAt: '2026-07-01T12:00:00.000+00:00' },
      { standFileId: 5, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 4,
    approvedCount: 3,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-01T09:00:00.000+00:00',
    updatedAt: '2026-07-01T12:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c17',
    standId: 310,
    standName: 'D-03',
    salonName: 'Salon 4',
    firmaId: null,
    firmaName: null,
    dekoratorFirmaId: null,
    dekoratorFirmaName: null,
    standTypeId: null,
    standHeightId: null,
    projectHeightNumber: null,
    standArea: null,
    floorInfo: null,
    statusId: null,
    dealStatus: null,
    financeApproval: null,
    financeText: null,
    serinProje: false,
    requiredFileIds: [],
    fileApprovals: [],
    requiredCount: 0,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-03T09:00:00.000+00:00',
    updatedAt: '2026-06-03T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c18',
    standId: 311,
    standName: 'A-104',
    salonName: 'Salon 1',
    firmaId: 63,
    firmaName: 'Kristal Cam Sanayi A.Ş.',
    dekoratorFirmaId: 66,
    dekoratorFirmaName: 'Yıldız Dekorasyon Ltd.',
    standTypeId: 5,
    standHeightId: 1,
    projectHeightNumber: 2.50,
    standArea: 18,
    floorInfo: 'Tek Katlı',
    statusId: 3,
    dealStatus: 3,
    financeApproval: 2,
    financeText: 'Katılmıyor.',
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [],
    requiredCount: 3,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 2 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-15T09:00:00.000+00:00',
    updatedAt: '2026-06-15T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c19',
    standId: 312,
    standName: 'B-08',
    salonName: 'Salon 2',
    firmaId: 64,
    firmaName: 'Orion Otomotiv Yedek Parça',
    dekoratorFirmaId: 89,
    dekoratorFirmaName: 'ModernStand İnş.',
    standTypeId: 6,
    standHeightId: 3,
    projectHeightNumber: 5.00,
    standArea: 40,
    floorInfo: 'Çift Katlı',
    statusId: 4,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: false,
    requiredFileIds: [1, 2, 3, 4],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-05T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-06-05T11:00:00.000+00:00' },
      { standFileId: 3, status: 1, approvedBy: 7, updatedAt: '2026-06-05T12:00:00.000+00:00' },
      { standFileId: 4, status: 1, approvedBy: 7, updatedAt: '2026-06-05T13:00:00.000+00:00' },
    ],
    requiredCount: 4,
    approvedCount: 4,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-05T09:00:00.000+00:00',
    updatedAt: '2026-06-05T13:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c1a',
    standId: 313,
    standName: 'A-105',
    salonName: 'Salon 1',
    firmaId: 65,
    firmaName: 'Baltık Kimya Sanayi Ltd.',
    dekoratorFirmaId: 66,
    dekoratorFirmaName: 'Yıldız Dekorasyon Ltd.',
    standTypeId: 5,
    standHeightId: 2,
    projectHeightNumber: 3.20,
    standArea: 21,
    floorInfo: 'Tek Katlı',
    statusId: 1,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-28T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-06-28T11:00:00.000+00:00' },
      { standFileId: 3, status: 1, approvedBy: 7, updatedAt: '2026-06-28T12:00:00.000+00:00' },
    ],
    requiredCount: 3,
    approvedCount: 3,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-28T09:00:00.000+00:00',
    updatedAt: '2026-06-28T12:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c1b',
    standId: 314,
    standName: 'B-09',
    salonName: 'Salon 2',
    firmaId: 66,
    firmaName: 'Efsane Mobilya Dekorasyon A.Ş.',
    dekoratorFirmaId: 152,
    dekoratorFirmaName: 'Zirve Stant Yapı',
    standTypeId: 6,
    standHeightId: 2,
    projectHeightNumber: 3.00,
    standArea: 24,
    floorInfo: 'Tek Katlı',
    statusId: 2,
    dealStatus: 1,
    financeApproval: 0,
    financeText: null,
    serinProje: true,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-07-02T10:00:00.000+00:00' },
      { standFileId: 2, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 3,
    approvedCount: 1,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-02T09:00:00.000+00:00',
    updatedAt: '2026-07-02T10:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c1c',
    standId: 315,
    standName: 'B-10',
    salonName: 'Salon 2',
    firmaId: 67,
    firmaName: 'Kadence Yazılım ve Teknoloji',
    dekoratorFirmaId: 152,
    dekoratorFirmaName: 'Zirve Stant Yapı',
    standTypeId: 5,
    standHeightId: 1,
    projectHeightNumber: 2.50,
    standArea: 15,
    floorInfo: 'Tek Katlı',
    statusId: 4,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: false,
    requiredFileIds: [1, 2],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-14T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-06-14T11:00:00.000+00:00' },
    ],
    requiredCount: 2,
    approvedCount: 2,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-14T09:00:00.000+00:00',
    updatedAt: '2026-06-14T11:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c1d',
    standId: 316,
    standName: 'C-17',
    salonName: 'Salon 3',
    firmaId: 68,
    firmaName: 'Yeşim Tarım Ürünleri Ltd.',
    dekoratorFirmaId: 130,
    dekoratorFirmaName: 'Prestij Fuarcılık Dekorasyon',
    standTypeId: 5,
    standHeightId: 2,
    projectHeightNumber: 3.00,
    standArea: 19,
    floorInfo: 'Tek Katlı',
    statusId: 3,
    dealStatus: 3,
    financeApproval: 2,
    financeText: 'Katılmıyor.',
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [],
    requiredCount: 3,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 2 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-16T09:00:00.000+00:00',
    updatedAt: '2026-06-16T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c1e',
    standId: 317,
    standName: 'D-04',
    salonName: 'Salon 4',
    firmaId: 69,
    firmaName: 'Cesur Metal İşleme San.',
    dekoratorFirmaId: 141,
    dekoratorFirmaName: 'MaxExpo Yapı Dekorasyon',
    standTypeId: 6,
    standHeightId: 3,
    projectHeightNumber: 4.80,
    standArea: 34,
    floorInfo: 'Çift Katlı',
    statusId: 1,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: false,
    requiredFileIds: [1, 2, 3, 4],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-07-03T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-07-03T11:00:00.000+00:00' },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 4, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 4,
    approvedCount: 2,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-03T09:00:00.000+00:00',
    updatedAt: '2026-07-03T11:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c1f',
    standId: 318,
    standName: 'E-01',
    salonName: 'Salon 5',
    firmaId: 70,
    firmaName: 'Panorama Turizm A.Ş.',
    dekoratorFirmaId: 163,
    dekoratorFirmaName: 'Atlas Fuar Dekorasyon',
    standTypeId: 5,
    standHeightId: 1,
    projectHeightNumber: 2.50,
    standArea: 17,
    floorInfo: 'Tek Katlı',
    statusId: 2,
    dealStatus: 2,
    financeApproval: 0,
    financeText: null,
    serinProje: false,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [],
    requiredCount: 3,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-20T09:00:00.000+00:00',
    updatedAt: '2026-06-20T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c20',
    standId: 319,
    standName: 'E-02',
    salonName: 'Salon 5',
    firmaId: null,
    firmaName: null,
    dekoratorFirmaId: null,
    dekoratorFirmaName: null,
    standTypeId: null,
    standHeightId: null,
    projectHeightNumber: null,
    standArea: null,
    floorInfo: null,
    statusId: null,
    dealStatus: null,
    financeApproval: null,
    financeText: null,
    serinProje: false,
    requiredFileIds: [],
    fileApprovals: [],
    requiredCount: 0,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-04T09:00:00.000+00:00',
    updatedAt: '2026-06-04T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c21',
    standId: 320,
    standName: 'E-03',
    salonName: 'Salon 5',
    firmaId: 71,
    firmaName: 'Kutup Soğutma Sistemleri',
    dekoratorFirmaId: 163,
    dekoratorFirmaName: 'Atlas Fuar Dekorasyon',
    standTypeId: 6,
    standHeightId: 2,
    projectHeightNumber: 3.50,
    standArea: 27,
    floorInfo: 'Tek Katlı',
    statusId: 4,
    dealStatus: 1,
    financeApproval: 1,
    financeText: 'Onaylandı.',
    serinProje: true,
    requiredFileIds: [1, 2, 3],
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-22T10:00:00.000+00:00' },
      { standFileId: 2, status: 1, approvedBy: 7, updatedAt: '2026-06-22T11:00:00.000+00:00' },
      { standFileId: 3, status: 1, approvedBy: 7, updatedAt: '2026-06-22T12:00:00.000+00:00' },
    ],
    requiredCount: 3,
    approvedCount: 3,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 1 },
      { key: 'statik', label: 'Statik Taahütname', status: 1 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 1 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-22T09:00:00.000+00:00',
    updatedAt: '2026-06-22T12:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c22',
    standId: 321,
    standName: 'A-106',
    salonName: 'Salon 1',
    firmaId: 72,
    firmaName: 'Mercan Deniz Ürünleri Ltd.',
    dekoratorFirmaId: 89,
    dekoratorFirmaName: 'ModernStand İnş.',
    standTypeId: 5,
    standHeightId: 1,
    projectHeightNumber: 2.50,
    standArea: 16,
    floorInfo: 'Tek Katlı',
    statusId: 2,
    dealStatus: 1,
    financeApproval: 0,
    financeText: null,
    serinProje: false,
    requiredFileIds: [1, 2],
    fileApprovals: [
      { standFileId: 1, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 2, status: 0, approvedBy: null, updatedAt: null },
    ],
    requiredCount: 2,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [
      { key: 'isg', label: 'İSG Taahütname', status: 0 },
      { key: 'statik', label: 'Statik Taahütname', status: 0 },
      { key: 'elektrik', label: 'Elektrik Taahütname', status: 0 },
    ],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-07-04T09:00:00.000+00:00',
    updatedAt: '2026-07-04T09:00:00.000+00:00',
  },
  {
    id: '665f1a2b3c4d5e6f7a8b9c23',
    standId: 322,
    standName: 'C-18',
    salonName: 'Salon 3',
    firmaId: null,
    firmaName: null,
    dekoratorFirmaId: null,
    dekoratorFirmaName: null,
    standTypeId: null,
    standHeightId: null,
    projectHeightNumber: null,
    standArea: null,
    floorInfo: null,
    statusId: null,
    dealStatus: null,
    financeApproval: null,
    financeText: null,
    serinProje: false,
    requiredFileIds: [],
    fileApprovals: [],
    requiredCount: 0,
    approvedCount: 0,
    uploadedFiles: [],
    textContents: [],
    commitments: [],
    revisions: [],
    sustainability: null,
    deleted: false,
    fuarId: _mockFuarId,
    createdAt: '2026-06-06T09:00:00.000+00:00',
    updatedAt: '2026-06-06T09:00:00.000+00:00',
  },
];

/**
 * "Stantlar" liste/kart görünümü (StandListPage.jsx) referans görselinden çıkarılan
 * alanlar — standNo/standCode (küçük kare rozetteki iki satır), firmaNation
 * (Yerli/Yabancı rozeti, firmaService.js'teki 1=Yerli kuralıyla aynı), standCategory
 * (Paket/Pavilyon/Özel rozeti) ve üç bağımsız durum bayrağı (hasDebt, constructionPermitGranted,
 * workPermitGranted — her biri kendi rengini taşır, üçünden biri "kötü" ise satır/kart kenarlığı
 * kırmızıya döner, dekoratör atanmamışsa da aynı şekilde — bu üç bayrak artık pill olarak
 * gösterilmiyor ama kenarlık mantığını (hasIssue) beslemeye devam eder). Proje kaydı olmayan
 * standlar (firmaId null) bu alanlara ihtiyaç duymaz. Ayrı bir override haritası olarak tutulur
 * ki yukarıdaki 23 kaydın tamamını elle genişletmek yerine standId üzerinden tek noktadan eklensin.
 *
 * 2026-07-10 liste kartı revizyonu ile eklenen alanlar:
 * - firmaShortName: standın üzerindeki kısa görünen ad (örn. "Acme Mobilya") — firmaName
 *   (örn. "Acme Mobilya A.Ş.") tüzel unvanın kısaltılmamış hali, ikisi ayrı alanlar.
 * - projectType: 'moduler' | 'ahsap' | 'diger' — proje tipi pill'i.
 * - projectStructure: projectType'a bağlı alt tip — moduler için 'maxima'|'octonorm',
 *   ahsap için 'mdf'|'sunta'|'agac'; diger için tanımsız (pill gösterilmez).
 * - fileApprovals override (306, 314): dosya durumu bildirim göstergesinde "reddedildi"
 *   (status: 2) durumunu da örneklemek için eklendi — temel veride hiç status:2 yoktu.
 */
const _standListDesignOverrides = {
  300: { standNo: 1, standCode: '1A11', firmaNation: 1, standCategory: 'paket',    hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Acme Mobilya',     projectType: 'moduler', projectStructure: 'maxima' },
  301: { standNo: 2, standCode: '1B12', firmaNation: 2, standCategory: 'pavilyon', hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'TechShow',         projectType: 'moduler', projectStructure: 'octonorm' },
  302: { standNo: 1, standCode: '2A21', firmaNation: 2, standCategory: 'ozel',     hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Global Expo',      projectType: 'ahsap',   projectStructure: 'mdf' },
  305: { standNo: 3, standCode: '1C13', firmaNation: 2, standCategory: 'paket',    hasDebt: false, constructionPermitGranted: true,  workPermitGranted: false,
    firmaShortName: 'Nordic Style',     projectType: 'ahsap',   projectStructure: 'sunta' },
  306: { standNo: 2, standCode: '2B22', firmaNation: 1, standCategory: 'pavilyon', hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Anka Havacılık',   projectType: 'diger',   projectStructure: null,
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-06-27T10:00:00.000+00:00' },
      { standFileId: 2, status: 2, approvedBy: 7, updatedAt: '2026-06-27T11:00:00.000+00:00' },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
      { standFileId: 4, status: 0, approvedBy: null, updatedAt: null },
    ] },
  307: { standNo: 1, standCode: '3A31', firmaNation: 1, standCategory: 'paket',    hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Prestij Gıda',     projectType: 'moduler', projectStructure: 'maxima' },
  308: { standNo: 2, standCode: '3B32', firmaNation: 1, standCategory: 'pavilyon', hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Vega Enerji',      projectType: 'ahsap',   projectStructure: 'agac' },
  309: { standNo: 1, standCode: '4A41', firmaNation: 1, standCategory: 'paket',    hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Lotus Tekstil',    projectType: 'moduler', projectStructure: 'octonorm' },
  311: { standNo: 4, standCode: '1D14', firmaNation: 1, standCategory: 'ozel',     hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Kristal Cam',      projectType: 'diger',   projectStructure: null },
  312: { standNo: 3, standCode: '2C23', firmaNation: 2, standCategory: 'pavilyon', hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Orion Otomotiv',   projectType: 'ahsap',   projectStructure: 'mdf' },
  313: { standNo: 5, standCode: '1E15', firmaNation: 1, standCategory: 'paket',    hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Baltık Kimya',     projectType: 'moduler', projectStructure: 'maxima' },
  314: { standNo: 4, standCode: '2D24', firmaNation: 1, standCategory: 'pavilyon', hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Efsane Mobilya',   projectType: 'ahsap',   projectStructure: 'sunta',
    fileApprovals: [
      { standFileId: 1, status: 1, approvedBy: 7, updatedAt: '2026-07-02T10:00:00.000+00:00' },
      { standFileId: 2, status: 2, approvedBy: 7, updatedAt: '2026-07-02T11:00:00.000+00:00' },
      { standFileId: 3, status: 0, approvedBy: null, updatedAt: null },
    ] },
  315: { standNo: 5, standCode: '2E25', firmaNation: 2, standCategory: 'ozel',     hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Kadence Yazılım',  projectType: 'moduler', projectStructure: 'octonorm' },
  316: { standNo: 3, standCode: '3C33', firmaNation: 1, standCategory: 'paket',    hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Yeşim Tarım',      projectType: 'ahsap',   projectStructure: 'agac' },
  317: { standNo: 2, standCode: '4B42', firmaNation: 1, standCategory: 'pavilyon', hasDebt: false, constructionPermitGranted: true,  workPermitGranted: false,
    firmaShortName: 'Cesur Metal',      projectType: 'diger',   projectStructure: null },
  318: { standNo: 1, standCode: '5A51', firmaNation: 2, standCategory: 'ozel',     hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Panorama Turizm',  projectType: 'moduler', projectStructure: 'maxima' },
  320: { standNo: 2, standCode: '5B52', firmaNation: 1, standCategory: 'paket',    hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Kutup Soğutma',    projectType: 'ahsap',   projectStructure: 'mdf' },
  321: { standNo: 6, standCode: '1F16', firmaNation: 2, standCategory: 'pavilyon', hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'Mercan Deniz',     projectType: 'moduler', projectStructure: 'octonorm' },
  // ─── "Test Decorator" (firmaId 500) müşteri roster'ı — Firma Ana Sayfa dashboard'ı ───
  // `tabelaAdi` — standCode'dan (organizatörün atadığı salon/stant kodu, ör. "4J44")
  // AYRI bir alan: standın üzerindeki tabelada yazan, katılımcı firmanın seçtiği
  // serbest metin (ör. Coca-Cola İçecek A.Ş. standında "Fanta" yazması gibi — firma
  // kendi ürün/markasını sergiliyor olabilir). 2026-07-31 revizyonu (`/revise-page`)
  // öncesinde bu ikisi (standCode ve "Stand Tabela Adı" etiketi) yanlışlıkla aynı
  // alanmış gibi kullanılıyordu, bu düzeltmeyle ayrıştırıldı. 326 kasıtlı olarak
  // tabelaAdi'sız bırakıldı — "veri girilmedi" durumunu örneklemek için (zaten proje
  // bilgisi de deklare edilmemiş bir stand).
  323: { standNo: 10, standCode: '4J44', firmaNation: 1, standCategory: 'paket',    hasDebt: false, constructionPermitGranted: true,  workPermitGranted: true,
    firmaShortName: 'Çarşıbaşı Kozmetik', projectType: 'moduler', projectStructure: 'maxima', tabelaAdi: 'İpek Kozmetik' },
  324: { standNo: 8,  standCode: '2K22', firmaNation: 1, standCategory: 'pavilyon', hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'ABC Tekstil',        projectType: 'ahsap',   projectStructure: 'mdf', tabelaAdi: 'ABC Home Tekstil' },
  325: { standNo: 3,  standCode: '1L19', firmaNation: 1, standCategory: 'paket',    hasDebt: true,  constructionPermitGranted: false, workPermitGranted: false,
    firmaShortName: 'DEF Mobilya',        projectType: 'diger',   projectStructure: null, tabelaAdi: 'DEF Concept Mobilya' },
  // 326: proje henüz deklare edilmedi (bkz. yukarısı) — projectType/projectStructure/
  // floorInfo kasıtlı olarak yok (dekoratör Proje Beyanı modalında girecek). standCategory
  // ise burada VAR: Stand Türü admin tarafından proje beyanından ÖNCE atanır, Proje Beyanı
  // modalındaki salt okunur "Stand Türü" alanının dolu görünmesi için (bkz. dosya sonu
  // createStandProject'in 2026-07-31 notu).
  326: { standNo: 6, standCode: '4M46', firmaShortName: 'Yeşil Bahçe Peyzaj', standCategory: 'ozel' },
};

const _mockStands = _mockStandsRaw.map((s) => (
  _standListDesignOverrides[s.standId] ? { ...s, ...(_standListDesignOverrides[s.standId]) } : s
));

// Proje Evrakları sekmesinde bir dosyanın "seçili" (choosenToGenerate) rozeti artık
// commitments[].fileId üzerinden türetilir — ham kayıtlarda elle işaretlenmiş
// choosenToGenerate değerleri burada gerçek bağlantıya göre normalize edilir, aksi
// halde hiçbir taahhütnameye bağlanmamış bir dosya sayfa ilk açıldığında yanlışlıkla
// "seçili" görünebilir.
_mockStands.forEach((s) => {
  const chosenFileIds = new Set((s.commitments || []).map((c) => c.fileId).filter(Boolean));
  (s.uploadedFiles || []).forEach((f) => { f.choosenToGenerate = chosenFileIds.has(f.id); });
});

/**
 * Fuardaki tüm standlar (proje kaydı olan/olmayan dahil).
 * GET /stands/overview?fuarId=3&search=&page=1&limit=20&decoratorFirmaId=66&firmaId=55
 * @param {Object} params - { fuarId, search, page, limit, decoratorFirmaId, firmaId }
 *
 * `/stands/overview` backend'de yok (bkz. getStandOverviewDetail'in dosya başı yorumu) —
 * `forceLive:true` verildiğinde bunun yerine gerçek roster uç noktası kullanılır:
 * GET /standTalep/list (fuarId + decoratorFirmaId, 0-based page/size — bkz.
 * firmaTalepListService.js'teki aynı 1-based↔0-based dönüşüm).
 *
 * 2026-08-29 (bkz. docs/guides/missing-pills-guide.md): pil alanları ARTIK bu satırda.
 * standCategory/floorInfo/projectType/projectStructure (stand_type ağacının 4 seviyesi,
 * id+ad), standCode/firmaShortName/firmaNation/tabelaAdi, standArea/projectHeightNumber/kw,
 * constructionPermit/commitments/workPermit — hepsi roster çağrısında geliyor, sayfa
 * başına üç toplu okumada. Liste render'ı için standId köprüsüne ARTIK GEREK YOK.
 *
 * Köprüde SADECE şunlar kaldı: fileApprovals/uploadedFiles (belge kutucukları) ve
 * projeBeyani (Proje Beyanı kapısı + beyan modalının kök stand tipi). Sayfalar bunları
 * ikinci, ENGELLEMEYEN bir aşamada çekiyor — piller ilk boyamada roster'dan çizilir,
 * belge kutucukları köprü dönünce dolar (bkz. FirmaProjelerListPage.jsx/
 * FirmaStandListPage.jsx/StandListPage.jsx'teki aynı desen). standOverviewService.js
 * projectService.js'i hâlâ import etmiyor, köprü SAYFA seviyesinde kurulur.
 * hasDebt burada `/list` satırının kendi legacy borç bayraklarından
 * (waterDebt/electricityDebt/airDebt/internetDebt) türetilir — getStandOverviewDetail'in
 * canlı dalındaki gerçek `/borcDurumu` kaynağından FARKLI ama ekstra bir çağrı gerektirmiyor,
 * liste satırı için yeterli bir sinyal.
 */
export async function getStandOverviewList(params = {}) {
  const {
    search = '', page = 1, limit = 20, decoratorFirmaId, firmaId, fuarId, forceLive = false,
    // Roster'ın kendi filtreleri (bkz. FE-StandTalepController-guide.md §list) — yalnızca
    // canlı dalda gönderilir. hasDebt SADECE true iken filtreler; false "hepsi" demek.
    salonId, hasDebt, isProblem, isForbidden,
  } = params;

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();

    // fuarId'ye göre filtrelenmiyor — bkz. _mockFuarId üstündeki not.
    let records = [..._mockStands];
    if (decoratorFirmaId) records = records.filter((s) => String(s.dekoratorFirmaId) === String(decoratorFirmaId));
    if (firmaId) records = records.filter((s) => String(s.firmaId) === String(firmaId));
    if (search) {
      const q = search.toLowerCase();
      records = records.filter((s) =>
        s.standName?.toLowerCase().includes(q) ||
        s.salonName?.toLowerCase().includes(q) ||
        (s.firmaName || '').toLowerCase().includes(q) ||
        (s.dekoratorFirmaName || '').toLowerCase().includes(q)
      );
    }

    const total = records.length;
    const start = (page - 1) * limit;
    records = records.slice(start, start + limit);

    return { total, page, limit, records };
  }

  if (forceLive) {
    try {
      const response = await axios.get(`${JAVA_BACKEND_API_URL}/standTalep/list`, {
        params: {
          fuarId, decoratorFirmaId, firmaId, search: search || undefined, page: page - 1, size: limit,
          salonId: salonId || undefined,
          hasDebt: hasDebt ? true : undefined,
          isProblem: isProblem ? 1 : undefined,
          isForbidden: isForbidden ? 1 : undefined,
        },
        headers: getAuthHeader(),
      });
      const data = response.data;
      const records = (data.data || []).map((r) => mapRosterRow(r, fuarId));
      return { total: data.totalCount, page, limit, records };
    } catch (error) {
      console.error('Error fetching stand overview list (live):', error);
      throw error;
    }
  }

  try {
    const response = await axios.get(BASE, { params, headers: getAuthHeader() });
    return response.data; // { total, page, limit, records }
  } catch (error) {
    console.error('Error fetching stand overview list:', error);
    throw error;
  }
}

/**
 * `GET /standTalep/list`'in bir satırını (StandListItemDTO) sayfaların okuduğu şekle
 * çevirir. getStandOverviewList ile getStandRosterRow BUNU PAYLAŞIR — pil alanlarının
 * anlamı (null/0/false ayrımları, izin makineleri) tek yerde tanımlı olsun diye; liste
 * ile detay sayfasının 2026-08-29'da olduğu gibi tekrar birbirinden kayması bu yüzden
 * artık mümkün değil.
 */
function mapRosterRow(r, fuarId) {
  return {
    id: String(r.standId),
    standId: r.standId,
    // §8: standNo BİLEREK gönderilmiyor ve gönderilmeyecek — onu tutan bir kolon yok
    // ve "11AF43" bir numara değil, kod. standCode'u etiket, standId'yi sayısal
    // tutamaç olarak kullanın (bkz. missing-pills-guide.md §8).
    standNo: null,
    standCode: r.standCode ?? null,
    standName: r.standName,
    salonName: r.salonName,
    // Düzenle popup'ı salonId'yi PUT /standTalep/update'e geri gönderiyor
    // (salon değişmediyse anahtarı hiç göndermiyor — kısmi güncelleme).
    salonId: r.salonId ?? null,
    firmaId: r.katilimciFirmaId,
    firmaName: r.katilimciFirmaName,
    firmaShortName: r.firmaShortName ?? null,
    // 1 Yerli · 2 Yabancı · null bilinmiyor. Roster'ın kendi alanı — ayrı bir
    // firma çağrısına gerek yok (eski ONE_EXTRA_CALL notu geçersiz).
    firmaNation: r.firmaNation ?? null,
    dekoratorFirmaId: r.dekoratorFirmaId,
    dekoratorFirmaName: r.dekoratorFirmaName,
    // #425 / #424 (backend #423): /list'e `firmaId` gönderildiğinde (firma portalı) her satır o
    // firmanın BU standdaki rollerini taşır — firmaTypeIds [1 Katılımcı · 3 Dekoratör · >=4 Taşeron],
    // firmaRolleri ["KATILIMCI"|"DEKORATOR"|"TASERON"]. `firmaId` gönderilmediyse (staff listeleri,
    // decoratorFirmaId'li eski çağrı) null — "rol bilinmiyor", sayfalar Dekoratör varsayar.
    firmaTypeIds: r.firmaTypeIds ?? null,
    firmaRolleri: r.firmaRolleri ?? null,
    // Yeni kolon — mevcut hiçbir standda dolu değil, kimse yazana kadar null.
    tabelaAdi: r.tabelaAdi ?? null,

    // ── stand_type ağacının dört seviyesi (missing-pills-guide.md §4) ────────────
    // Her seviye id + AD olarak gelir. AD gösterim metnidir (staff düzenleyebilir),
    // id kararlıdır. projectStructure KAPALI bir küme DEĞİL (TRUSS, Örümcek Stand,
    // iki ayrı Sunta girdisi... ve 'agac' hiç yok) — gelen adı olduğu gibi bas.
    // ⚠️ projectType bugün HER satırda null: dört seviyeli ağaç canlı ama 17.077
    // tipli proje satırının hepsi hâlâ eski üç seviyeli ağacı işaret ediyor. Pil
    // gizlensin, fallback UYDURMA — yanlış Ahşap/Modüler etiketi yoktan kötüdür.
    standTypeId: r.standTypeId ?? null,
    standTypeName: r.standTypeName ?? null,
    standCategoryId: r.standCategoryId ?? null,
    standCategory: r.standCategory ?? null,
    floorInfoId: r.floorInfoId ?? null,
    floorInfo: r.floorInfo ?? null,
    projectTypeId: r.projectTypeId ?? null,
    projectType: r.projectType ?? null,
    projectStructureId: r.projectStructureId ?? null,
    projectStructure: r.projectStructure ?? null,

    // ⚠️ standArea KROKİDEN gelir — dekoratörün Proje Beyanı'nda yazdığı m² DEĞİL
    // (karar: 2026-08-27). Beyan edilen m² fuar kuralına karşı DENETLENMEK için
    // var, gerçek diye gösterilmek için değil; sessizce büyüyen stand ödenmemiş
    // zemindir. Kapsam kısmi (fuar 295'te 947 standın 363'ü, 292/298'de HİÇ) —
    // null normal durum, hata değil. "—" bas, ASLA "0 m²".
    // Beyan edilen sayı gerekiyorsa (uyuşmazlık uyarısı vb.) hâlâ
    // projectService.getProjectByStandId → projeBeyani.declaredAreaM2.
    standArea: r.standArea ?? null,
    // Backend artık DB'deki 0.00'ı null'a çeviriyor (17.098 satırın 5.602'si) —
    // oradaki sıfır "hiç girilmemiş"in legacy PHP karşılığı, 0 m boyunda stand değil.
    projectHeightNumber: r.projectHeightNumber ?? null,
    // null = elektrik istenmedi. ASLA 0'a çevirme: "0 kW" rozeti "hiç istemedi"den
    // FARKLI bir cümledir.
    kw: r.kw ?? null,

    hasDebt: !!(r.waterDebt || r.electricityDebt || r.airDebt || r.internetDebt),
    // stand_detail bayrakları + firmanın (standın değil) yasaklı olup olmadığı.
    standIsForbidden: !!r.standIsForbidden,
    standIsProblem: !!r.standIsProblem,
    katilimciIsForbidden: !!r.katilimciIsForbidden,

    // ── İnşa İzni: boolean DEĞİL, dört durumlu makine (§5) ──────────────────────
    // "İnşa İznine Gerek yok" ile "İzin Verilmedi" ikisi de "granted:false" ama
    // zıt anlamlı — bu yüzden ham nesne olduğu gibi taşınıyor. Renk için
    // constructionPermit.badge KULLAN (DB'den gelir, staff bakımını yapar);
    // statusId'den renk YENİDEN TÜRETME. statusId 0 → statusName/badge null.
    constructionPermit: r.constructionPermit ?? null,
    // Geriye dönük uyumluluk: eski boolean'ı okuyan yerler için türetilmiş görünüm.
    // 1 Gerek yok · 4 İzin Verildi · 7 Nehir İSGM uygun gördü = izin var sayılır.
    constructionPermitGranted: CONSTRUCTION_PERMIT_GRANTED_STATUS_IDS.includes(
      r.constructionPermit?.statusId
    ),
    // Taahhütnameler ARTIK yüklenmiyor: KAYNAK belgeler yükleniyor (İSG←3D Köşe,
    // Elektrik←Kuşbakışı, Statik←Statik Hesap), inceleyen kişi doğru dosyayı seçiyor,
    // sistem taahhütnameyi üretiyor. state: NO_SOURCE|SOURCE_UPLOADED|CHOSEN|
    // GENERATED|SIGNATURE_REQUIRED|SIGNED — zincir bugün CHOSEN'da duruyor (PDF
    // üretimi henüz yok). İzni BUNDAN HESAPLAMA, cevap constructionPermit.statusId.
    commitments: r.commitments ?? [],
    // ── Teknik Talepler (Elektrik/Su/Hava/İnternet) — backlog #69(3) ────────────
    // Her zaman 4 kalem, hasRequest:false dahil (backend TeknikTalepDTO'nun kendi
    // sözleşmesi) — kart tarafı istenmemiş kategoriyi soluk gri gösterir, eksik değil.
    teknik: r.teknik ?? [],
    // ── İş İzni (§6) ───────────────────────────────────────────────────────────
    // ⚠️ jobApproved/jobTotal İŞ İZNİ SAYACI DEĞİL — her stand_job satırını sayarlar
    // (919 legacy + 49 hizmet + 467 iş izni). Kanıt: stand 40609, jobTotal 8 /
    // jobApproved 8 ama hiç izin talep etmemiş. Rollup'ı kullan.
    workPermit: r.workPermit ?? null,
    // granted = EN AZ BİR talep VE hepsi onaylı. requested, "hiç istemedi" ile
    // "reddedildi"yi ayırmak için var.
    workPermitGranted: r.workPermit?.granted ?? false,
    jobTotal: r.jobTotal ?? 0,
    jobApproved: r.jobApproved ?? 0,
    jobPending: r.jobPending ?? 0,
    jobDeclined: r.jobDeclined ?? 0,

    // Roster'ın taşımadığı, hâlâ SADECE projectService.getProjectByStandId'den gelen
    // alanlar — sayfa seviyesinde ikinci bir aşamada doldurulur (bkz. dosya başı
    // yorumu). Belge kutucukları (fileApprovals/uploadedFiles) ve Proje Beyanı
    // kapısı (projeBeyani.declared) bunlara bağlı.
    requiredCount: 0,
    approvedCount: 0,
    fileApprovals: [],
    uploadedFiles: [],
    projectDeclared: undefined,
    deleted: false,
    fuarId,
  };
}

/**
 * Bir fuarda standlara atanmış, tekrarsız dekoratör firma listesi — bir
 * dekoratör birden çok katılımcı firma için çalışabildiğinden, bu listedeki
 * her satır tek bir dekoratör firmasını (ve o firmanın kaç stand işlettiğini)
 * temsil eder. Dekoratör seçim dropdown'ını beslemek için kullanılır.
 *
 * Backlog #69(1), 2026-09-22: `GET /stands/overview/decorators` bu dosyadaki yorumda
 * uzun süredir vardı ama backend'de HİÇ karşılığı yoktu — bu fonksiyon her zaman mock
 * dönüyordu. Standlar sayfasının Dekoratör filtresi CANLI veriye ihtiyaç duyunca gerçek
 * uç bulunamadı; StandTalepController'a (zaten /standTalep/list'i barındırıyor) yeni bir
 * GET /standTalep/decorators eklendi, bu fonksiyon ona yönlendirildi.
 * GET /standTalep/decorators?fuarId=3
 */
export async function getDecoratorFirmaOptions(params = {}, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();

    // fuarId'ye göre filtrelenmiyor — bkz. _mockFuarId üstündeki not.
    let records = [..._mockStands];

    const byId = new Map();
    records.forEach((s) => {
      if (!s.dekoratorFirmaId) return;
      const existing = byId.get(s.dekoratorFirmaId);
      if (existing) {
        existing.standCount += 1;
      } else {
        byId.set(s.dekoratorFirmaId, {
          firmaId: s.dekoratorFirmaId,
          firmaName: s.dekoratorFirmaName,
          standCount: 1,
        });
      }
    });

    return Array.from(byId.values()).sort((a, b) => a.firmaName.localeCompare(b.firmaName));
  }

  try {
    const response = await axios.get(`${JAVA_BACKEND_API_URL}/standTalep/decorators`, { params, headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching decorator firma options:', error);
    throw error;
  }
}

/**
 * Staff — yeni bir stand satırı açar (henüz firma/proje bağlı değil). Gerçek
 * backend'de `stand` + `salon_stand_firma_to_fuar` (firma_id=0 placeholder) yazar,
 * `stand_height`/`stand_is_salearea` hardcoded 0'dır — stand tipi/yüksekliği bu
 * uçta değil, ayrı bir proje kaydında yaşar (bkz. StandListPage.jsx "Yeni Stand"
 * modalı: bu çağrıdan sonra assignFirmaToStand + gerekirse projectService.createProject
 * art arda çağrılır).
 * POST /standTalep/create — standCode, fuarId, salonId, firmaEsle
 *
 * ⚠️ GÖVDE ALANI 2026-09-03'te `standName` → `standCode` OLDU ve gönderilen şey
 * artık tam ad DEĞİL, holün İÇİNDEKİ koddur: `{standCode:"Z99", salonId:1}` →
 * `"1.Z99"`. Adın hol yarısını sunucu `salon.salon_name`'den kuruyor; biz kurmuyoruz
 * (bkz. docs/guides/FE-StandTalepController-guide.md "Stand naming"). Boş/whitespace
 * kod → 400. `standName` yalnızca güncellenmemiş çağıranlar için bir yedek ve SADECE
 * `standCode` anahtarı HİÇ YOKKEN okunuyor — bu yüzden burada boş kodu göndermiyoruz.
 *
 * ⚠️ Yanıttaki `standName`/`standCode`/`salonName` KULLANILMALI, ad yeniden
 * türetilmemeli: adı biz seçmedik, hol seçti.
 */
export async function createStand({ standCode, standName, fuarId, salonId, salonName }, { forceLive = false } = {}) {
  // standCode verilmediyse eski çağrı biçimine saygı: tam addan kodu çıkar.
  const kod = String(standCode ?? standCodeFromName(standName) ?? '').trim();

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const standId = Date.now();
    const mockSalonName = salonName || `Salon ${salonId}`;
    const composedName = composeStandName(mockSalonName, kod);
    const mockRecord = {
      id: `mock-new-${standId}`, standId, standName: composedName, standCode: kod,
      salonName: mockSalonName,
      firmaId: null, firmaName: null, dekoratorFirmaId: null, dekoratorFirmaName: null,
      standTypeId: null, standHeightId: null, projectHeightNumber: null, standArea: null,
      floorInfo: null, statusId: null, dealStatus: null, financeApproval: null, financeText: null,
      serinProje: false, requiredFileIds: [], fileApprovals: [], requiredCount: 0, approvedCount: 0,
      uploadedFiles: [], textContents: [], commitments: [], firmalar: [], denetimler: [],
      kontrolFormu: null, revisions: [], sustainability: null, projectDeclared: false,
      deleted: false, fuarId, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    _mockStands.push(mockRecord);
    return {
      success: true, standId, standName: composedName, standCode: kod,
      salonName: mockSalonName, firmaEsleRedirect: false,
    };
  }
  try {
    const res = await axios.post(`${JAVA_BACKEND_API_URL}/standTalep/create`, {
      standCode: kod, fuarId, salonId, firmaEsle: 0,
    }, { headers: getAuthHeader() });
    return res.data;
  } catch (error) {
    console.error('Error creating stand:', error);
    throw error;
  }
}

/**
 * Staff — standın kodunu / salonunu / tabela adını günceller.
 * PUT /standTalep/update/{standId} — standCode, salonId, tabelaAdi (üçü de opsiyonel)
 *
 * ⚠️ GERÇEK bir kısmi güncelleme, ama üç alanın "yok" semantiği AYNI DEĞİL
 * (bkz. FE-changed-payloads-2026-08-28-guide.md §1):
 *   - `standCode`: gönderilmezse dokunulmaz; BOŞ gönderilirse de dokunulmaz
 *     (adsız stand kimsenin istediği bir durum değil).
 *   - `salonId`: gönderilmezse dokunulmaz. Yazılırsa standın TÜM
 *     salon_stand_firma_to_fuar satırlarını günceller.
 *   - `tabelaAdi`: varlığı `containsKey` ile test ediliyor → anahtarı hiç
 *     GÖNDERMEMEK "dokunma", `""`/`null` göndermek ise "SİL" demek. Tabela adını
 *     kaldırmak gerçek bir düzenleme olduğu için boş string bir emirdir, no-op değil.
 *
 * ⚠️ 2026-09-03: gövde alanı `standName` → `standCode` oldu (holün İÇİNDEKİ kod) ve
 * SALON DEĞİŞTİRMEK STANDI YENİDEN ADLANDIRIR — `{salonId:12}` tek başına
 * "11.A10" → "12.A10" yapar, çünkü adın hol yarısı çağıranın malı değil. Taşımadan
 * sonra satırı yenileyin; yanıt yeni adı TAŞIMIYOR.
 * ⚠️ `standName` yalnızca bir yedek ve SADECE `standCode` anahtarı hiç yokken
 * okunuyor; gönderilirse de kod gibi işlenir (holün öneki soyulur).
 * ⚠️ Uzunluk kontrolü YOK: 255'ten uzun tabelaAdi MySQL'de 500 olarak patlar.
 * ⚠️ Değerler JSON STRING olmalı — sayı gönderilirse (Integer) cast'i 500 verir.
 * ⚠️ Bilinmeyen standId → 404 (eski rehberin "200 döner" iddiası düzeltildi).
 */
export async function updateStand(standId, patch = {}, { forceLive = false } = {}) {
  // Sadece GERÇEKTEN gönderilmek istenen anahtarları taşıyoruz — undefined olan
  // her alan "dokunma" demek. tabelaAdi'nin null/"" hâli bilerek korunuyor.
  const body = {};
  // standCode kazanır; standName yalnızca eski çağıranlar için ve koda çevrilerek.
  const kod = patch.standCode !== undefined
    ? patch.standCode
    : (patch.standName !== undefined ? standCodeFromName(patch.standName) : undefined);
  if (kod !== undefined && String(kod).trim() !== '') {
    body.standCode = String(kod).trim();
  }
  if (patch.salonId !== undefined && patch.salonId !== null && patch.salonId !== '') {
    body.salonId = Number(patch.salonId);
  }
  if (patch.tabelaAdi !== undefined) {
    body.tabelaAdi = patch.tabelaAdi === null ? null : String(patch.tabelaAdi);
  }

  if (Object.keys(body).length === 0) return { result: true, message: i18n.t("services:standOverviewService.degisiklikYok"), data: null };

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const stand = _mockStands.find((s) => String(s.standId) === String(standId));
    if (!stand) throw new Error('Stand bulunamadı');
    if (body.salonId !== undefined) stand.salonName = patch.salonName || `Salon ${body.salonId}`;
    // Adı canlıda olduğu gibi HOLDEN kur: kod değişmese bile salon değiştiyse ad değişir.
    if (body.standCode !== undefined) stand.standCode = body.standCode;
    if (body.standCode !== undefined || body.salonId !== undefined) {
      stand.standName = composeStandName(stand.salonName, stand.standCode ?? standCodeFromName(stand.standName));
    }
    if (body.tabelaAdi !== undefined) stand.tabelaAdi = body.tabelaAdi || null;
    stand.updatedAt = new Date().toISOString();
    return { result: true, message: `Stand updated: ${standId}`, data: null };
  }

  try {
    const res = await axios.put(
      `${JAVA_BACKEND_API_URL}/standTalep/update/${standId}`,
      body,
      { headers: getAuthHeader() }
    );
    return res.data;
  } catch (error) {
    console.error('Error updating stand:', error);
    throw error;
  }
}

/**
 * Fuarın salonları — Yeni Stand / Stand Düzenle popup'larındaki salon seçici için.
 * GET /fuar/{fuarId} → fuarSalons[]
 *
 * krokiService.getSalonlarByFuar ile aynı kaynak ama SAYIM YAPMAZ: orası salon
 * başına stand sayısı için ek istekler atıyor (fuar 295'te salon sayısı kadar),
 * bir modalın açılışında ödenecek bedel değil.
 *
 * ⚠️ FuarController'ın auth bypass'ı YOK — oturum düşükse 401 gelir. Bu yüzden
 * hata YUTULUYOR ve boş dizi dönüyor: popup salon seçici yerine elle salon id
 * girişine düşer, tamamen kilitlenmez.
 * ⚠️ Salon ADI çoğu fuarda salon NUMARASIDIR ("1", "2", "11a"), yani stand adının
 * başındaki hol numarası; ama "ANA FUAYE" gibi metinsel adlar da var — bu yüzden
 * hol numarası popup'ta ayrı ve DÜZENLENEBİLİR bir alan.
 */
export async function getSalonOptions(fuarId, { forceLive = false } = {}) {
  if (!fuarId) return [];

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    return [
      { salonId: 1, salonName: '1' },
      { salonId: 2, salonName: '2' },
      { salonId: 3, salonName: '3' },
      { salonId: 4, salonName: '4' },
    ];
  }

  try {
    const res = await axios.get(`${JAVA_BACKEND_API_URL}/fuar/${fuarId}`, { headers: getAuthHeader() });
    const salons = res.data?.fuarSalons || [];
    return salons
      .map((s) => ({
        salonId: s.id ?? s.salonId ?? null,
        // Uç snake_case (`salon_name`) döndürüyor; bazı uçlar camelCase — ikisi de.
        salonName: s.salon_name ?? s.salonName ?? (s.id != null ? `Salon ${s.id}` : '—'),
      }))
      .filter((s) => s.salonId != null)
      .sort((a, b) => {
        const an = Number(a.salonName); const bn = Number(b.salonName);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
        if (!Number.isNaN(an)) return -1;
        if (!Number.isNaN(bn)) return 1;
        return String(a.salonName).localeCompare(String(b.salonName), 'tr');
      });
  } catch (error) {
    console.error('Error fetching salon options (elle giriş devrede):', error);
    return [];
  }
}

/**
 * Staff — bir firmayı standa bağlar (katılımcı ya da dekoratör). İkisi de aynı uç,
 * sadece firmaTypeId farklı (1 katılımcı, 3 dekoratör) — bkz. StandListPage.jsx
 * "Yeni Stand" modalı, iki ayrı çağrı olarak kullanılıyor.
 * POST /standTalep/assignFirma/{standId} — firmaId, firmaTypeId, relationLevel, fuarId
 */
export async function assignFirmaToStand(standId, { firmaId, firmaTypeId, fuarId, relationLevel = 1 }, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const stand = _mockStands.find((s) => String(s.standId) === String(standId));
    if (stand) {
      if (firmaTypeId === 3) { stand.dekoratorFirmaId = firmaId; stand.dekoratorFirmaName = `Firma #${firmaId}`; }
      else { stand.firmaId = firmaId; stand.firmaName = `Firma #${firmaId}`; }
    }
    return { success: true, standId, firmaId };
  }
  try {
    const res = await axios.post(`${JAVA_BACKEND_API_URL}/standTalep/assignFirma/${standId}`, {
      firmaId, firmaTypeId, relationLevel, fuarId,
    }, { headers: getAuthHeader() });
    return res.data;
  } catch (error) {
    console.error('Error assigning firma to stand:', error);
    throw error;
  }
}

/**
 * TEK bir standın roster satırı — `GET /standTalep/list`'in bilgi pilleri (İnşa İzni
 * durum makinesi, İş İzni rollup'ı, stand_type ağacının dört seviyesi, firmaNation,
 * standCode/tabelaAdi/firmaShortName, standArea/projectHeightNumber/kw, commitments[]).
 *
 * ⚠️ NEDEN ARAMA İLE: bu alanların HİÇBİRİ `GET /standTalep/detay/{standId}`'de yok
 * (canlı doğrulandı 2026-09-04 — detay'ın anahtarları: detail/firms/fuarId/jobs/notes/
 * photos/salonId/salonName/standDimensions/standId/standName/talepler) ve `/list`'in
 * standId filtresi YOK. `search` stand adı VEYA katılımcı firma adında LIKE %…% yapar,
 * o yüzden standName ile arayıp dönen sayfada standId'yi kendimiz eşleştiriyoruz —
 * ada güvenmiyoruz, sadece aramayı daraltmak için kullanıyoruz.
 *
 * Bulunamazsa null döner (hata DEĞİL): stand silinmiş, adı değişmiş ya da arama
 * beklenenden fazla satır döndürmüş olabilir. Çağıran taraf pilleri gizler, uydurmaz.
 */
export async function getStandRosterRow(standId, { fuarId, standName } = {}) {
  if (!fuarId || !standId) return null;
  try {
    const res = await axios.get(`${JAVA_BACKEND_API_URL}/standTalep/list`, {
      params: { fuarId, search: standName || undefined, page: 0, size: 200 },
      headers: getAuthHeader(),
    });
    const hit = (res.data?.data || []).find((r) => Number(r.standId) === Number(standId));
    return hit ? mapRosterRow(hit, fuarId) : null;
  } catch (error) {
    console.error('Error fetching stand roster row:', error);
    return null;
  }
}

/**
 * Zenginleştirilmiş stand detayı — belgeler, revizyonlar, sürdürülebilirlik dahil.
 * GET /stands/overview/{id}
 *
 * ⚠️ `/stands/overview/**` backend'de HİÇ yok (bkz. docs/guides/FE-ProjectController-
 * beyani-revisions-guide.md "Asked for but not built"). `forceLive:true` verildiğinde
 * (Stand Listesi tarafının canlı standId'leri için — bkz. StandOverviewDetailPage.jsx)
 * bunun yerine iki bağımsız gerçek endpoint birleştirilir: standTalep/detay (firma/talep
 * bilgisi, MySQL) + borcDurumu. Proje/belge alanları (requiredFileIds, fileApprovals,
 * uploadedFiles, commitments, revisions, standTypeId...) BİLEREK burada YOK — bunlar
 * standId köprüsüyle projectService.getProjectByStandId'den gelir; iki servis dosyası
 * birbirini import etmez (bkz. projectService.js bölüm 11), köprü SAYFA seviyesinde
 * kurulur — StandOverviewDetailPage.jsx artık bu alanları `bridgedProject`'ten okuyor.
 * standArea/floorInfo/standCode/hasDebt'in bir kısmı backend'de hâlâ yok (bkz.
 * docs/BACKEND_TODO.md "Needs a backend decision") — null/hesaplanabilen kadarıyla döner.
 */
export async function getStandOverviewDetail(id, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const stand = _mockStands.find((s) => s.id === id);
    if (!stand) throw new Error('Stand bulunamadı');
    return { ...stand };
  }

  if (forceLive) {
    const standId = id;
    // FDS-Backend #306: the backend's local auth bypass is gone, so the request must carry the
    // logged-in user's own session. Overriding X-user-id with 18 (a staff id, for the full firms[])
    // now 401s for everyone but user 18. Staff still get the full list; a company gets its own view.
    // WAS: const liveHeaders = { ...getAuthHeader(), 'X-user-id': '18' }; // staff view — tam firms[]
    const liveHeaders = getAuthHeader();
    try {
      const [detayRes, borcRes] = await Promise.all([
        axios.get(`${JAVA_BACKEND_API_URL}/standTalep/detay/${standId}`, { headers: liveHeaders }),
        axios.get(`${JAVA_BACKEND_API_URL}/standTalep/borcDurumu/${standId}`, { headers: liveHeaders })
          .catch(() => ({ data: [] })), // yardımcı zenginleştirme — başarısız olursa sessizce boş
      ]);
      const detay = detayRes.data;
      const firms = detay.firms || [];
      const katilimci = firms.find((f) => f.firma_type_id === 1);
      const dekorator = firms.find((f) => f.firma_type_id === 3 && f.relation_level === 1);
      // ⚠️ Canlı yanıt guide'ın belgelediği düz BorcDurumu[] değil — {categories, hasDebt,
      // legacyDebtWarning, legacyDebtFlags} zarfı, ve hasDebt zaten sunucuda hesaplanmış
      // olarak geliyor (bkz. GET /standTalep/borcDurumu/{standId}, canlı doğrulandı 2026-08-10).
      const hasDebt = borcRes.data?.hasDebt ?? false;

      // Bilgi pilleri SADECE roster'da (`/standTalep/list`) yaşıyor — detay onların
      // hiçbirini taşımıyor. Stand Listesi ile Stand Detayı'nın aynı cümleyi kurması
      // için o satırı burada çekip aşağıya seriyoruz; bulunamazsa (null) alanlar
      // yokmuş gibi davranır ve sayfa pilleri gizler — uydurulmuş bir "İzin Verilmedi"
      // basmaktansa hiç basmamak doğru (bkz. missing-pills-guide.md §5).
      const roster = await getStandRosterRow(standId, {
        fuarId: detay.fuarId, standName: detay.standName,
      });

      return {
        // Roster'ın pilleri önce serilir ki aşağıdaki detay alanları (standName,
        // salonName, firma bağları, hasDebt...) her zaman kazansın — detay bu
        // alanlarda daha zengin (firms[] üzerinden dekoratör/katılımcı ayrımı).
        ...(roster || {}),
        id: String(standId),
        standId: detay.standId,
        standName: detay.standName,
        salonName: detay.salonName,
        // Gömülü kroki (fdsvector /embed/stand-plan) salonu id ile ister, adla değil
        // — bkz. Components/Kroki/KrokiVectorEmbed.jsx. `detay` bu alanı vermezse
        // null kalır ve sayfa krokiyi id'siz açmaz.
        salonId: detay.salonId ?? null,
        firmaId: katilimci?.firma_id ?? null,
        firmaName: katilimci?.firma_name ?? null,
        dekoratorFirmaId: dekorator?.firma_id ?? null,
        dekoratorFirmaName: dekorator?.firma_name ?? null,
        // floorInfo: backend'de alan yok (bkz. dosya başı yorumu). standArea artık
        // stand_kroki.stand_dimensions'tan (varchar, "150" gibi m² metni) türetiliyor —
        // sayısal değilse (boş/"WxD" gibi eski serbest metin) null'a düşer.
        // Roster standArea'yı zaten sunucu tarafında (aynı kroki kaynağından)
        // çözülmüş veriyor; yoksa stand_kroki.stand_dimensions varchar'ından
        // türetmeye düşeriz. Sayısal değilse (boş/"WxD" gibi eski serbest metin) null.
        standArea: roster?.standArea ?? (
          Number.isFinite(Number(detay.standDimensions)) && detay.standDimensions
            ? Number(detay.standDimensions) : null
        ),
        // floorInfo artık roster'da var (stand_type ağacının 2. seviyesi) — spread
        // onu getiriyor, burada null'a EZMİYORUZ.
        hasDebt,
        // Stand Durumu sekmesinin (standDurumuService.getStandDurumuOzet'in problemliStand
        // okuduğu) admin'in 8 alanlık salt-okunur kopyası — GET /standTalep/detay'ın `detail`
        // alt objesinden (stand_detail satırı) bire bir eşleniyor, `detail` bazı standlarda
        // (stand_detail satırı olmayanlarda) null olabilir (bkz. StandTalepController guide).
        standDurumu: detay.detail ? {
          problemliStand: detay.detail.stand_detail_is_problem ? 'evet' : 'hayir',
          icTesisat: detay.detail.stand_detail_ic_tesisat_onayi ? 'var' : 'yok',
          yasakliStand: detay.detail.stand_is_forbidden ? 'evet' : 'hayir',
          islakImza: detay.detail.stand_islak_imza ? 'var' : 'yok',
          alanBorcu: detay.detail.stand_detail_alan_borcu ? 'var' : 'yok',
          projeBorcu: detay.detail.stand_detail_proje_borcu ? 'var' : 'yok',
          internetTalebi: detay.detail.stand_detail_internet_talebi ? 'var' : 'yok',
          camKullanimi: detay.detail.stand_detail_cam_kullanimi ? 'var' : 'yok',
        } : null,
        // Saha modülü denetim geçmişi/kontrol formu — bu controller'ın kapsamı dışında
        // (InspectionHierarchyController, henüz belgelenmedi), boş/null kalır.
        denetimler: [],
        kontrolFormu: null,
        // Fotoğraflar — stand_photo satırlarının salt-okunur karşılığı (mock'un
        // {id,caption,uploadedAt} şekline eşlenir).
        photos: (detay.photos || []).map((p) => ({
          id: p.id, caption: p.standPhotoName || i18n.t("services:standOverviewService.fotograf"), uploadedAt: p.standPhotoInsertdate,
        })),
        // Stant Firmaları — GET /detay'ın `firms[]`'i zaten neredeyse aynı şekilde
        // (firma_id/firma_name/firma_type_name/relation_level → eslesmeSeviyesi).
        firmalar: (detay.firms || []).map((f) => ({
          firmaId: f.firma_id, firmaName: f.firma_name, firmaTypeName: f.firma_type_name,
          eslesmeSeviyesi: f.relation_level,
        })),
        deleted: false,
        fuarId: detay.fuarId,
        updatedAt: null,
      };
    } catch (error) {
      console.error('Error fetching stand overview detail (live):', error);
      throw error;
    }
  }

  try {
    const response = await axios.get(`${BASE}/${id}`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error fetching stand overview detail:', error);
    throw error;
  }
}

/**
 * Proje Evrakları sekmesinde bir dosyanın onay durumunu inceleyen kişi doğrudan set
 * eder (Uygun/Red). status: 0=Beklemede, 1=Uygun, 2=Red — reddedilen dosya evrak
 * havuzundan silinmez, sadece durumu değişir.
 * PUT /stands/overview/{id}/files/{fileId}/approval
 */
export async function setUploadedFileApproval(id, fileId, status, { forceLive = false, projectId, standFileId } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const stand = _mockStands.find((s) => s.id === id);
    const file = stand?.uploadedFiles?.find((f) => f.id === fileId);
    if (file) file.approvalStatus = Number(status);
    return { success: true };
  }

  if (forceLive) {
    // Gerçek onay proje BAŞLIK'ı (standFileId) düzeyindedir, tek bir yüklenen dosya
    // değil (bkz. docs/BACKEND_TODO.md projectService bölümü) — projectService.js'in
    // by-stand köprüsüyle bulduğu proje id'sine (projectId) ve dosyanın standFileId'sine
    // ihtiyaç var, ikisi de standOverviewService.js'in bilmediği alanlar; çağıran sayfa
    // (StandOverviewDetailPage.jsx) bridgedProject'ten çözüp buraya geçirir.
    if (!projectId || standFileId == null) throw new Error(i18n.t("services:standOverviewService.onayIcinProjeIdVe"));
    try {
      const response = await axios.put(
        `${JAVA_BACKEND_API_URL}/projects/${projectId}/file-approval`,
        null,
        { params: { standFileId, status }, headers: getAuthHeader() },
      );
      return response.data;
    } catch (error) {
      console.error('Error setting uploaded file approval (live):', error);
      throw error;
    }
  }

  try {
    const response = await axios.put(`${BASE}/${id}/files/${fileId}/approval`, null, {
      params: { status },
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error setting uploaded file approval:', error);
    throw error;
  }
}

/**
 * #213: ONE uploaded file reviewed on its own, with the reason the company will see on a rejection - the per-file
 * counterpart of setUploadedFileApproval above (which only reaches the whole document title). status: 0 Beklemede,
 * 1 Onaylandi, 2 Reddedildi. Live: PUT /projects/{projectId}/files/{fileId}/approval?status=&rejectReason= (staff
 * only; it does not change the title's approval, and anything but 1 also un-chooses a chosen commitment source).
 */
export async function setUploadedFileReview(id, fileId, status, rejectReason, { forceLive = false, projectId } = {}) {
  const reason = status === 2 && rejectReason && String(rejectReason).trim() ? String(rejectReason).trim() : undefined;
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const stand = _mockStands.find((s) => s.id === id);
    const file = stand?.uploadedFiles?.find((f) => f.id === fileId);
    if (file) {
      file.approvalStatus = Number(status);
      file.reviewedAt = new Date().toISOString();
      file.rejectReason = reason ?? null;
    }
    return { success: true };
  }

  if (forceLive) {
    if (!projectId) throw new Error(i18n.t("services:standOverviewService.dosyaIncelemeIcinProjeId"));
    try {
      const response = await axios.put(
        `${JAVA_BACKEND_API_URL}/projects/${projectId}/files/${fileId}/approval`,
        null,
        { params: { status, rejectReason: reason }, headers: getAuthHeader() },
      );
      return response.data;
    } catch (error) {
      console.error('Error reviewing an uploaded file (live):', error);
      throw error;
    }
  }

  try {
    const response = await axios.put(`${BASE}/${id}/files/${fileId}/approval`, null, {
      params: { status, rejectReason: reason },
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error reviewing an uploaded file:', error);
    throw error;
  }
}

/**
 * Bir dosyayı belirli bir Proje Taahütnamesi için "ana belge" seç/kaldır (toggle) —
 * o taahhütnameye daha önce bağlı başka bir dosya varsa yerini alır. choosenToGenerate
 * rozeti bu çağrı sonrası tüm dosyalar için otomatik yeniden hesaplanır.
 * PUT /stands/overview/{id}/commitments/{commitmentKey}/file
 */
export async function chooseFileForCommitment(id, commitmentKey, fileId, { forceLive = false, projectId } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const stand = _mockStands.find((s) => s.id === id);
    const commitment = stand?.commitments?.find((c) => c.key === commitmentKey);
    if (commitment) {
      commitment.fileId = commitment.fileId === fileId ? null : fileId;
      const chosenFileIds = new Set((stand.commitments || []).map((c) => c.fileId).filter(Boolean));
      (stand.uploadedFiles || []).forEach((f) => { f.choosenToGenerate = chosenFileIds.has(f.id); });
    }
    return { success: true };
  }

  if (forceLive) {
    // commitmentKey'in gerçek backend'de karşılığı yok — hangi taahhütname olduğu
    // zaten dosyanın kendi standFileId(5)/subStandFileId'siyle belli, bu yüzden burada
    // yoksayılıyor. PUT /{id}/files/{fileId}/choose düz bir toggle (bkz. proje guide).
    if (!projectId) throw new Error(i18n.t("services:standOverviewService.anaBelgeSecimiIcinProje"));
    try {
      const response = await axios.put(
        `${JAVA_BACKEND_API_URL}/projects/${projectId}/files/${fileId}/choose`,
        null,
        { headers: getAuthHeader() },
      );
      return response.data;
    } catch (error) {
      console.error('Error choosing file for commitment (live):', error);
      throw error;
    }
  }

  try {
    const response = await axios.put(`${BASE}/${id}/commitments/${commitmentKey}/file`, null, {
      params: { fileId },
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error('Error choosing file for commitment:', error);
    throw error;
  }
}

/**
 * Firma tarafı — bir belge grubuna (standFileId, ör. Statik Hesap/Teknik Talep
 * Planı/Proje Görselleri) yeni bir dosya yükler. Yüklenen dosya her zaman "Beklemede"
 * (approvalStatus: 0) durumunda başlar, onay/red admin tarafında ayrı bir akıştır
 * (setUploadedFileApproval). Proje Görselleri (standFileId 5) için subStandFileId
 * ile hangi köşe/açı olduğu belirtilir, diğer belge tiplerinde null geçilir.
 * POST /stands/overview/{id}/files (multipart: standFileId, subStandFileId, file)
 */
export async function uploadStandFile(id, standFileId, subStandFileId, file, { forceLive = false, projectId } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const stand = _mockStands.find((s) => s.id === id);
    if (!stand) throw new Error('Stand bulunamadı');
    const uploadedBy = Number(localStorage.getItem('userId')) || stand.firmaId;
    const newFile = {
      id: `mock-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      standFileId,
      subStandFileId: subStandFileId ?? null,
      storedFileName: `${Date.now()}_${file.name}`,
      originalFileName: file.name,
      approvalStatus: 0,
      choosenToGenerate: false,
      uploadedBy,
      uploadedAt: new Date().toISOString(),
      deleted: false,
      deletedBy: null,
      deletedAt: null,
    };
    stand.uploadedFiles = [...(stand.uploadedFiles || []), newFile];
    return { success: true, file: newFile };
  }

  if (forceLive) {
    // `/stands/overview/{id}/files` (bu dosyanın BASE'i) gerçek backend'de yok
    // (bkz. FE-ProjectController-project-guide.md "Asked for but not built") —
    // gerçek yükleme projectService.js'in POST /{id}/files'ı, proje Mongo _id'siyle
    // (bkz. setUploadedFileApproval/chooseFileForCommitment'taki aynı projectId deseni).
    if (!projectId) throw new Error(i18n.t("services:standOverviewService.dosyaYuklemekIcinProjeId"));
    try {
      const formData = new FormData();
      formData.append('standFileId', standFileId);
      if (subStandFileId !== null && subStandFileId !== undefined) formData.append('subStandFileId', subStandFileId);
      formData.append('file', file);
      const response = await axios.post(`${JAVA_BACKEND_API_URL}/projects/${projectId}/files`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading stand file (live):', error);
      throw error;
    }
  }

  // #713: POST /stands/overview/{id}/files does not exist on the gateway, and with USE_MOCK_DATA fixed at true this point is only
  // reached by a non-mock, non-live call, which nobody makes: a real upload is `forceLive` above.
  // WAS (until #713):
  // try {
  //   const formData = new FormData();
  //   formData.append('standFileId', standFileId);
  //   if (subStandFileId !== null && subStandFileId !== undefined) formData.append('subStandFileId', subStandFileId);
  //   formData.append('file', file);
  //   const response = await axios.post(`${BASE}/${id}/files`, formData, {
  //     headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
  //   });
  //   return response.data;
  // } catch (error) {
  //   console.error('Error uploading stand file:', error);
  //   throw error;
  // }
  throw new Error(i18n.t("services:standOverviewService.dosyaYuklemekIcinProjeId"));
}

/**
 * Firma tarafı — the stand's Kuşbakışı / Teknik Talep Planı drawing (#696 follow-up). One drawing, two places: see kusbakisiYardimci.js. Live it
 * is filed under 3-D Görseller > Kuşbakışı (what the Talep Gönder gate and the Elektrik taahhütname read) and under Elektrik Planı (the required
 * document the portal always wrote); a mock stand only has the second. The Kuşbakışı write goes first, so a failure there leaves nothing half
 * written; a failure of the second leaves the Kuşbakışı stored and the error to the caller, who can upload again (the newest file of either place wins).
 * Resolves with the answer of the last write.
 */
export async function uploadKusbakisi(id, file, { forceLive = false, projectId } = {}) {
  const mock = USE_MOCK_DATA && !forceLive;
  let sonuc = null;
  for (const [standFileId, subStandFileId] of planYazimlari(!mock)) {
    sonuc = await uploadStandFile(id, standFileId, subStandFileId, file, { forceLive, projectId });
  }
  return sonuc;
}

/**
 * Firma tarafı — Stand Kısa Adı Değiştirme (FirmaStandDetailPage.jsx "Stant
 * Bilgileri" sekmesi). standCode, _standListDesignOverrides'ten gelen kozmetik
 * bir alan (StandThumbnail rozetinin alt satırı) — burada değiştirilebilir hale
 * getiriliyor.
 * PUT /stands/overview/{id}/code
 */
export async function updateStandCode(id, standCode) {
  if (USE_MOCK_DATA) {
    await mockDelay();
    const stand = _mockStands.find((s) => s.id === id);
    if (!stand) throw new Error('Stand bulunamadı');
    stand.standCode = standCode;
    stand.updatedAt = new Date().toISOString();
    return { ...stand };
  }

  try {
    const response = await axios.put(`${BASE}/${id}/code`, { standCode }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    console.error('Error updating stand code:', error);
    throw error;
  }
}

// wallWidths/wallHeights ({ [duvarId]: değer }) createStandProject'e aynı şekilde
// gelir — boş/null anahtarlar atılır, geri kalanı Number'a çevrilir. Genişlik
// yükseklikten daha önemli kabul edildiği için modalda önce soruluyor (bkz.
// FirmaProjelerListPage.jsx'teki 2026-07-31 "duvar genişliği" notu), ama saklama
// şekli ikisi için de birebir aynı.
// WAS (until #713): helper of the retired createStandProject (mock wall maps):
// function sanitizeWallMap(map) {
//   return map
//     ? Object.fromEntries(
//         Object.entries(map)
//           .filter(([, v]) => v != null && v !== '')
//           .map(([k, v]) => [k, Number(v)])
//       )
//     : null;
// }

/**
 * Firma tarafı, MOCK MOD — henüz proje bilgisi girilmemiş bir stand için
 * dekoratörün ilk proje beyanını yapması (bkz. FirmaProjelerListPage.jsx "Proje
 * Beyanı" modalı, dosya başındaki 2026-07-31 revizyon notu). standCategory (Stand
 * Türü) burada YOK — o zaten admin tarafından _standListDesignOverrides üzerinden
 * atanmış, salt okunur (bkz. aşağıdaki override 326). Zorunlu: floorInfo (Proje
 * Türü — 'Tek Katlı'/'Çift Katlı'), projectType ('ahsap'/'moduler'), projectStructure
 * (Proje Tipi — projectType'a bağlı alt tip), hMax, m2 (Proje Alanı — eski en×boy'un
 * yerini aldı, en/boy hiçbir yerde ayrı okunmadığı için kaldırıldı). Opsiyonel:
 * kw (2026-07-31'den beri — artık Ölçüler'de değil, tek kaynak modaldaki Teknik
 * Talep/Elektrik kartının switch'i; kapalıysa null gelir, "0 kW" gibi yanlış bir
 * StandInfoGrid rozeti oluşmasın diye Number(0) değil null yazılır), wallWidths +
 * wallHeights ({ [duvarId]: değer } — eski wallCount/wallHeight1/wallHeight2'nin
 * yerini aldı, bkz. sanitizeWallMap). standArea/projectHeightNumber m2/hMax'tan
 * türetilir ki kart üzerindeki mevcut StandInfoGrid alan/yükseklik kutuları başka
 * bir değişiklik gerekmeden dolsun.
 *
 * 2026-08-11: bu fonksiyonun eski `forceLive` dalı (proje kaydını KENDİ kendine
 * sabit bir leaf tipe — Octonorm Profil — açıp aynı anda kendi kendine beyan eden
 * kestirme) kaldırıldı; gerçek backend'de bu tek adımlı akış yok, iki ayrı adım var
 * (bkz. FE-ProjectController-beyani-revisions-guide.md bölüm A). Canlı köprüde artık:
 * proje kaydını (kök standTypeId + söz verilen yükseklik) staff
 * StandOverviewDetailPage.jsx'teki "Proje Kaydı Oluştur" ile açıyor
 * (projectService.createProject), dekoratör de kendi alt tipini gerçek kaskadla
 * FirmaProjelerListPage.jsx'teki CreateProjectModal üzerinden `projectService.declareProject`
 * ile beyan ediyor. Bu fonksiyon artık sadece MOCK modda kullanılıyor.
 * POST /stands/overview/{id}/declare
 */
// WAS (until #713): mock-only declaration writer; the live declaration is projectService.declareProject, and POST /stands/overview/{id}/declare is not on the gateway. No caller left:
// export async function createStandProject(id, info) {
//   const { floorInfo, projectType, projectStructure, hMax, m2, kw = null, wallWidths = null, wallHeights = null } = info;

//   if (USE_MOCK_DATA) {
//     await mockDelay();
//     const stand = _mockStands.find((s) => s.id === id);
//     if (!stand) throw new Error('Stand bulunamadı');
//     stand.floorInfo = floorInfo;
//     stand.projectType = projectType;
//     stand.projectStructure = projectStructure;
//     stand.hMax = Number(hMax);
//     stand.kw = kw != null && kw !== '' ? Number(kw) : null;
//     stand.wallWidths = sanitizeWallMap(wallWidths);
//     stand.wallHeights = sanitizeWallMap(wallHeights);
//     stand.projectHeightNumber = Number(hMax);
//     stand.standArea = Number(m2);
//     stand.projectDeclared = true;
//     stand.updatedAt = new Date().toISOString();
//     return { ...stand };
//   }

//   try {
//     const response = await axios.post(`${BASE}/${id}/declare`, info, { headers: getAuthHeader() });
//     return response.data;
//   } catch (error) {
//     console.error('Error creating stand project:', error);
//     throw error;
//   }
// }
